import React from 'react';
import ReactMarkdown from 'react-markdown';

import { motion } from "framer-motion";
import { useParams, useSearchParams, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ChatInterface, loadprofile, primeCallAudioFromUserGesture } from '../pages/Chats';
import { Base64, countryList, femaleeditquestions, fetchData, generateUUID, getMultiples, getLocalStorageItem, malequestions, packages, postData, removeLocalStorageItem, setLocalStorageItem, SmartImageComponent, timestamptodate, turntoacronym } from './misc';
import Signup from './signup';
import { ImageFormComponent } from './menimagesform';
import Cookies from 'js-cookie';
import AdminLayout from './AdminLayout';

import BronzeVideo from '../media/BRONZE PACKAGE.mp4';
import SilverVideo from '../media/SILVER PACKAGE.mp4';
import GoldVideo from '../media/GOLD PACKAGE.mp4';
import PlatinumVideo from '../media/PLATINUM PACKAGE.mp4';
import DiamondVideo from '../media/DIAMOND PACKAGE.mp4';
import EmeraldVideo from '../media/EMERALD PACKAGE.mp4';
import PackagesStructureVideo from '../media/PACKAGESSTRUCTURE.mp4';
import MatchPercentageVideo from '../media/MATCHPERCENTAGE.mp4';
import ProfileAnalysisVideo from '../media/PROFILEANALYSIS.mp4';
import EngagementGuidelinesImg from '../media/ENGAGEMENTGUIDELINES.jpeg';
import GetVerifiedLivenessImg from '../media/get-verified-liveness.png';
import { getSiteDomain } from '../utils/domain';
import { trackEvent } from '../utils/bridge.js';

function isVerifiedProfile(profile) {
    return profile?.verified_profile === true || profile?.verified_profile === 'true';
}

function VerifiedProfileBadge() {
    return <span className="ml-2 inline-flex items-center rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">Verified</span>;
}

function decodeProfileEmailParam(value) {
    if (!value) return '';
    try {
        return Base64.decode(value);
    } catch (err) {
        return value;
    }
}

function buildProfileViewTrackingContext(viewedProfile, viewerEmailParam) {
    const viewerEmail = decodeProfileEmailParam(viewerEmailParam);
    return {
        viewed_email: viewedProfile?.EMAIL || '',
        viewed_name: viewedProfile?.fullname1 || '',
        viewer_email: viewerEmail || '',
        package: viewedProfile?.package1 || '',
        gender: viewedProfile?.gender1 || '',
    };
}
const LIVENESS_MIN_RECORDING_MS = 10 * 1000;
const LIVENESS_PROMPT_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000;

function replaceWithDomainHomepage() {
    window.location.replace('/');
}

function validateAuthCookie() {
    const authCookie = Cookies.get('auth');
    if (authCookie === undefined) {
        replaceWithDomainHomepage();
        return () => {};
    }

    let cancelled = false;
    fetchData('/api/auth_cookie/current')
        .then((data) => {
            const expectedAuthCookie = data?.auth_cookie || 'current';
            if (!cancelled && authCookie !== expectedAuthCookie) {
                Cookies.remove('auth');
                replaceWithDomainHomepage();
            }
        })
        .catch((err) => console.error('Failed to validate auth cookie', err));

    return () => {
        cancelled = true;
    };
}

function AuthCookieGate({ children, adminOnly = false }) {
    const [authorized, setAuthorized] = React.useState(null);
    const authCookie = Cookies.get('auth');
    const emailCookie = Cookies.get('email');
    const location = useLocation();

    React.useEffect(() => {
        if (authCookie === undefined || (adminOnly && emailCookie !== 'jazeelakarima@gmail.com')) {
            setAuthorized(false);
            return () => {};
        }

        let cancelled = false;
        fetchData('/api/auth_cookie/current')
            .then((data) => {
                const expectedAuthCookie = data?.auth_cookie || 'current';
                if (!cancelled) {
                    const isAuthorized = authCookie === expectedAuthCookie;
                    if (!isAuthorized) {
                        Cookies.remove('auth');
                    }
                    setAuthorized(isAuthorized);
                }
            })
            .catch((err) => {
                console.error('Failed to validate auth cookie', err);
                setAuthorized(true);
            });

        return () => {
            cancelled = true;
        };
    }, [authCookie, emailCookie, adminOnly]);

    if (authorized === null) {
        return null;
    }

    const currentPath = `${location.pathname}${location.search}`;
    const loginPath = adminOnly
        ? `/sirri_api/login?redirect=${encodeURIComponent(currentPath)}`
        : '/login';

    return authorized ? <>{children}</> : <Navigate to={loginPath} replace />;
}

function hasCompletedLivenessVerification(profile) {
    return isVerifiedProfile(profile) || Boolean(profile?.livenessVideoUrl || profile?.liveness_video_submitted_at);
}

function shouldShowLivenessNotification(profile) {
    return Boolean(profile?.EMAIL) && !hasCompletedLivenessVerification(profile);
}

function livenessPromptDismissalKey(profile) {
    return `liveness_prompt_dismissed_at:${String(profile?.EMAIL || '').trim().toLowerCase()}`;
}

function shouldShowLivenessModal(profile, now = Date.now(), localDismissedAt = '') {
    if (!shouldShowLivenessNotification(profile)) return false;
    const dismissals = [profile.liveness_prompt_dismissed_at, localDismissedAt]
        .map(value => Date.parse(value || ''))
        .filter(value => Number.isFinite(value) && value <= now);
    return dismissals.length === 0 || now - Math.max(...dismissals) >= LIVENESS_PROMPT_COOLDOWN_MS;
}

function LivenessVerificationModal({ showModal, profile, setShowModal, onProfileUpdated }) {
    const videoRef = React.useRef(null);
    const previewUrlRef = React.useRef('');
    const mediaRecorderRef = React.useRef(null);
    const recordingStartedAtRef = React.useRef(0);
    const countdownIntervalRef = React.useRef(null);
    const autoStopTimeoutRef = React.useRef(null);
    const chunksRef = React.useRef([]);
    const activeRef = React.useRef(showModal);
    const openingCameraRef = React.useRef(false);
    const cameraRequestRef = React.useRef(0);
    const streamRef = React.useRef(null);
    const [isRecording, setIsRecording] = React.useState(false);
    const [recordingCountdown, setRecordingCountdown] = React.useState(LIVENESS_MIN_RECORDING_MS / 1000);
    const [recordedBlob, setRecordedBlob] = React.useState(null);
    const [statusMessage, setStatusMessage] = React.useState('');
    const [uploading, setUploading] = React.useState(false);
    const [showIntroStep, setShowIntroStep] = React.useState(true);

    React.useEffect(() => {
        activeRef.current = showModal;
        if (showModal) {
            setShowIntroStep(true);
            setIsRecording(false);
            setRecordedBlob(null);
            setStatusMessage('');
        }
        return releaseRecording;
    }, [showModal]);

    function releaseRecording() {
        activeRef.current = false;
        cameraRequestRef.current += 1;
        openingCameraRef.current = false;
        clearRecordingTimers();
        const recorder = mediaRecorderRef.current;
        if (recorder) {
            recorder.onstop = null;
            recorder.ondataavailable = null;
            if (recorder.state !== 'inactive') recorder.stop();
        }
        stopStreamTracks();
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = '';
    }

    function dismissLiveness() {
        releaseRecording();
        const dismissedAt = new Date().toISOString();
        const fields = { liveness_prompt_dismissed_at: dismissedAt };
        setLocalStorageItem(livenessPromptDismissalKey(profile), dismissedAt);
        if (onProfileUpdated) onProfileUpdated(fields);
        setShowModal(false);
        trackEvent('liveness_verification_dismissed', { email: profile?.EMAIL });
        saveProfileFields(fields).catch(error => console.error('Could not save liveness reminder dismissal:', error));
    }

    function handleModalVisibility(visible) {
        if (!visible) dismissLiveness();
        else setShowModal(true);
    }

    function clearRecordingTimers() {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        if (autoStopTimeoutRef.current) {
            clearTimeout(autoStopTimeoutRef.current);
            autoStopTimeoutRef.current = null;
        }
    }

    function stopStreamTracks() {
        const recorder = mediaRecorderRef.current;
        const stream = streamRef.current || recorder?.stream;
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
        }
        streamRef.current = null;
        if (videoRef.current) videoRef.current.srcObject = null;
    }

    async function saveProfileFields(fields) {
        if (!profile?.EMAIL) {
            return;
        }
        const formData = new FormData();
        formData.append('_lookup_EMAIL', profile.EMAIL);
        formData.append('EMAIL', profile.EMAIL);
        Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
        const response = await postData('/api/edit_data', formData, { headers: { 'content-type': 'multipart/form-data' } });
        if (response?.responsecode !== 200) {
            throw new Error('Could not save liveness verification status.');
        }
        if (onProfileUpdated) {
            onProfileUpdated(fields);
        }
    }

    function continueToRecording() {
        trackEvent('liveness_verification_get_verified_clicked', { email: profile?.EMAIL });
        setShowIntroStep(false);
    }

    async function startRecording() {
        if (!activeRef.current || openingCameraRef.current || isRecording || uploading) return;
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
            setStatusMessage('Your browser does not support camera recording. Please try another browser or device.');
            return;
        }
        openingCameraRef.current = true;
        const requestId = ++cameraRequestRef.current;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            if (!activeRef.current || requestId !== cameraRequestRef.current) {
                stream.getTracks().forEach(track => track.stop());
                return;
            }
            streamRef.current = stream;
            chunksRef.current = [];
            const recorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : undefined });
            recorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    chunksRef.current.push(event.data);
                }
            };
            recorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: 'video/webm' });
                if (previewUrlRef.current) {
                    URL.revokeObjectURL(previewUrlRef.current);
                }
                previewUrlRef.current = URL.createObjectURL(blob);
                setRecordedBlob(blob);
                if (videoRef.current) {
                    videoRef.current.srcObject = null;
                    videoRef.current.src = previewUrlRef.current;
                    videoRef.current.controls = true;
                }
                stopStreamTracks();
                setStatusMessage('Recording captured. Submit it for review when you are ready.');
            };
            mediaRecorderRef.current = recorder;
            recordingStartedAtRef.current = Date.now();
            if (videoRef.current) {
                videoRef.current.src = '';
                videoRef.current.srcObject = stream;
                videoRef.current.controls = false;
                videoRef.current.muted = true;
                videoRef.current.play();
            }
            setRecordedBlob(null);
            setRecordingCountdown(LIVENESS_MIN_RECORDING_MS / 1000);
            setStatusMessage('Recording... keep your face clearly in the camera. It will stop automatically after 10 seconds.');
            setIsRecording(true);
            recorder.start();
            countdownIntervalRef.current = setInterval(() => {
                const remaining = Math.max(0, Math.ceil((LIVENESS_MIN_RECORDING_MS - (Date.now() - recordingStartedAtRef.current)) / 1000));
                setRecordingCountdown(remaining);
            }, 250);
            autoStopTimeoutRef.current = setTimeout(() => {
                stopRecording(true);
            }, LIVENESS_MIN_RECORDING_MS);
            trackEvent('liveness_verification_recording_started', { email: profile?.EMAIL });
        } catch (error) {
            if (!activeRef.current || requestId !== cameraRequestRef.current) return;
            clearRecordingTimers();
            stopStreamTracks();
            setIsRecording(false);
            console.error('Could not start liveness recording:', error);
            setStatusMessage('Camera access was not granted. You can close this and try again later.');
        } finally {
            if (requestId === cameraRequestRef.current) openingCameraRef.current = false;
        }
    }

    function stopRecording(allowEarlyStop = false) {
        const recorder = mediaRecorderRef.current;
        if (!recorder || recorder.state === 'inactive') {
            return;
        }
        const elapsed = Date.now() - recordingStartedAtRef.current;
        if (!allowEarlyStop && elapsed < LIVENESS_MIN_RECORDING_MS) {
            const secondsLeft = Math.ceil((LIVENESS_MIN_RECORDING_MS - elapsed) / 1000);
            setStatusMessage(`Please keep recording for ${secondsLeft} more second${secondsLeft === 1 ? '' : 's'}.`);
            return;
        }
        clearRecordingTimers();
        setRecordingCountdown(0);
        recorder.stop();
        setIsRecording(false);
        trackEvent('liveness_verification_recording_stopped', { email: profile?.EMAIL, duration_ms: elapsed });
    }

    function getRecordingButtonText() {
        if (isRecording) {
            return `Recording ${recordingCountdown}`;
        }
        if (recordedBlob) {
            return 'Recording completed, start again?';
        }
        return 'Start recording';
    }

    async function submitRecording() {
        if (!recordedBlob) {
            setStatusMessage('Please record your liveness video first.');
            return;
        }
        setUploading(true);
        setStatusMessage('Uploading your liveness video for review...');
        try {
            const formData = new FormData();
            const safeEmailForFilename = String(profile?.EMAIL || 'unknown-email').trim().toLowerCase().replace(/[^a-z0-9@._-]+/gi, '_') || 'unknown-email';
            formData.append('video', recordedBlob, `liveness-${safeEmailForFilename}-${Date.now()}.webm`);
            formData.append('EMAIL', profile?.EMAIL || '');
            const response = await postData('/api/upload_chat_video', formData, { headers: { 'content-type': 'multipart/form-data' } });
            if (response?.responsecode !== 200 || !response.video_url) {
                throw new Error(response?.message || 'Upload failed');
            }
            await saveProfileFields({
                livenessVideoUrl: response.video_url,
                liveness_video_submitted_at: new Date().toISOString(),
                liveness_prompt_dismissed_at: new Date().toISOString(),
            });
            trackEvent('liveness_verification_completed', { email: profile?.EMAIL });
            setStatusMessage('Thank you. Once reviewed, you will receive a verified badge on your profile.');
            setShowModal(false);
        } catch (error) {
            console.error('Failed to upload liveness video:', error);
            setStatusMessage('The upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    }

    return (
        <GeneralModal showModal={showModal} maintitle="Quick liveness check" notdisableproceed={false} setShowModal={handleModalVisibility}>
            {showIntroStep
                ? <div className="text-black normal-case space-y-4">
                    <img src={GetVerifiedLivenessImg} alt="Get verified liveness check" className="mx-auto w-full max-w-2xl rounded-2xl shadow-lg" />
                    <p>Please submit a 10-second liveness video to complete profile verification.</p>
                    <div className="flex items-center justify-between gap-3">
                        <button type="button" className="rounded-full bg-emerald-700 px-5 py-2 font-semibold text-white" onClick={continueToRecording}>Get verified</button>
                    </div>
                </div>
                : <div className="text-black normal-case space-y-4">
                    <video ref={videoRef} className="w-full rounded-2xl bg-black max-h-80" playsInline muted={isRecording} />
                    <div className="flex flex-wrap gap-3">
                        <button type="button" className="rounded-full bg-[#512430] px-5 py-2 font-semibold text-white disabled:opacity-50" onClick={startRecording} disabled={isRecording || uploading}>{getRecordingButtonText()}</button>
                        <button type="button" className="rounded-full bg-emerald-700 px-5 py-2 font-semibold text-white disabled:opacity-50" onClick={submitRecording} disabled={!recordedBlob || uploading || isRecording}>Submit for review</button>
                    </div>
                    {statusMessage ? <p className="font-semibold text-[#512430]">{statusMessage}</p> : null}
                </div>
            }
            <button type="button" className="rounded-full bg-gray-200 px-5 py-2 font-semibold text-black" onClick={dismissLiveness}>Maybe later</button>
        </GeneralModal>
    );
}

export default function Profile(props) {
    const { profileid } = useParams();

    const siteDomain = getSiteDomain();

    const [profiledata, setProfileData] = React.useState({})
    const [womandata, setWomanData] = React.useState({nothing:"nothing"})

    const [loading, setLoading] = React.useState(true)

    const [individualchatvisible, setIndividualchatvisible] = React.useState(false)
    const [callSignal, setCallSignal] = React.useState(0)
    const [individualchattitle, setIndividualChatTitle] = React.useState({})

    const [showeditprofilemodal, setEditProfileModal] = React.useState(false)
    const [showeditimages, setEditimages] = React.useState(false)
    const [searchParams, setSearchParams] = useSearchParams()
    const profileViewTrackingRef = React.useRef(null)
    let navigate = useNavigate();

    function parseHiddenProfiles(hiddenProfilesValue) {
        if (Array.isArray(hiddenProfilesValue)) {
            return hiddenProfilesValue;
        }

        if (typeof hiddenProfilesValue === 'string' && hiddenProfilesValue.trim() !== '') {
            try {
                const parsedHiddenProfiles = JSON.parse(hiddenProfilesValue);
                if (Array.isArray(parsedHiddenProfiles)) {
                    return parsedHiddenProfiles;
                }
            } catch (err) {
                console.warn('Could not parse hidden_profiles for Profile route redirect:', err);
            }
        }

        return [];
    }

    function getPackageDurationDays(packageName) {
        const packageDays = {
            bronze: 30,
            silver: 60,
            gold: 90,
            platinum: 180,
            diamond: 360,
            emerald: 730,
            sapphire: 1095,
            ruby: 1460,
        };

        if (!packageName) {
            return null;
        }

        return packageDays[String(packageName).trim().toLowerCase()] ?? null;
    }

    function logProfileLoginAndExpiry(profile) {
        const lastLoginTimestamp = Number(profile?.lastlogin);
        const packageStartTimestamp = Number(profile?.timestamp);
        const packageDurationDays = getPackageDurationDays(profile?.package1);

        if (!Number.isNaN(lastLoginTimestamp) && Number.isFinite(lastLoginTimestamp)) {
            console.log('lastlogin timestamp (number):', lastLoginTimestamp);
            console.log('lastlogin date:', new Date(lastLoginTimestamp * 1000).toLocaleString());
        } else {
            console.log('lastlogin is missing or invalid:', profile?.lastlogin);
        }

        if (!Number.isNaN(packageStartTimestamp) && Number.isFinite(packageStartTimestamp) && packageDurationDays !== null) {
            const nowTimestamp = Date.now() / 1000;
            const expiryTimestamp = packageStartTimestamp + (packageDurationDays * 24 * 60 * 60);
            const daysUntilExpiry = Math.ceil((expiryTimestamp - nowTimestamp) / (24 * 60 * 60));

            console.log('package:', profile?.package1);
            console.log('days till package expiry:', Math.max(daysUntilExpiry, 0));
        } else {
            console.log('Unable to calculate package expiry days.', {
                package: profile?.package1,
                timestamp: profile?.timestamp,
            });
        }
    }

    function load() {
        console.clear();
        const url = `/api/getprofile/${profileid}`;
        const email = searchParams.get('EMAIL')
        removeLocalStorageItem('formData')
        
        fetchData(url).then(async (data) => {
            if (data.responsecode === 200 && data.data !== false) {
                const profileViewTrackingContext = buildProfileViewTrackingContext(data.data, email);
                profileViewTrackingRef.current = {
                    startedAt: Date.now(),
                    eventName: email ? 'match_profile_view_duration' : 'profile_view_duration',
                    context: profileViewTrackingContext,
                    finished: false,
                };
                trackEvent(email ? 'match_profile_viewed' : 'profile_viewed', profileViewTrackingContext)
                trackEvent(email ? 'match_profile_view_started' : 'profile_view_started', profileViewTrackingContext)
                setProfileData(data.data)
                logProfileLoginAndExpiry(data.data)

                if (email) {
                    const viewerProfileResponse = await fetchData(`/api/getprofile/${email}`);
                    const viewerProfile = viewerProfileResponse?.responsecode === 200 ? viewerProfileResponse?.data : null;
                    const hiddenProfiles = parseHiddenProfiles(viewerProfile?.hidden_profiles);
                    const viewedManName = String(data.data?.fullname1 || '').trim().toLowerCase();
                    const profileIsHidden = hiddenProfiles.some(
                        (hiddenName) => String(hiddenName || '').trim().toLowerCase() === viewedManName
                    );

                    if (profileIsHidden) {
                        trackEvent('profile_hidden_redirected')
                        navigate(`/personalprofile/${email}`);
                        return;
                    }
                }

                if (data.data.denialofservice && props.authenticate !== false) {
                    trackEvent('profile_view_denial_of_service_blocked')
                    alert("This profile is currently unavailable.")
                    navigate('/loginplease')
                    return
                }

                if (!email) {
                    setLoading(false)
                }
            } else {
                trackEvent('profile_view_failed')
                alert("Something went wrong. Please contact support. You will be redirected to the login page. If you are unable to do so afterwards please contact support.")
                window.location.href = "/login?redirect="+window.location.href
            }
        }).catch((error) => {
            console.error("Error fetching purgatory data:", error);
            throw error;
        });

        if (email) {
            setLoading(true)
            const womanurl = `/api/getprofile/${email}`;
            console.log("Load url:", womanurl)
            loadprofile(womanurl, setWomanData, setLoading)
        }

    }

    function deleteimages() {
        trackEvent('profile_images_delete_clicked')
        const url = "/api/deletepics?EMAIL="+profiledata.EMAIL;

        fetchData(url).then((data) => {
            console.log(data);
            if (data.responsecode === 200) {
                trackEvent('profile_images_delete_success')
                alert("Images deleted sucessfully. You can now upload new ones.")
                setEditimages(false)
            } else {
                alert("Something went wrong. Please contact support.")
            }
        }).catch((error) => {
            console.error("Error fetching purgatory data:", error);
            throw error;
        });

    }

    function messageManFunction() {
        trackEvent('match_profile_message_clicked', buildProfileViewTrackingContext(profiledata, searchParams.get('EMAIL')))
        setIndividualChatTitle(profiledata)
        setIndividualchatvisible(true)
    }

    function editManFunction() {
        trackEvent('profile_edit_clicked')
        setEditProfileModal(true)
    }

    function deleteProfileFunction() {
        trackEvent('profile_delete_clicked')
        const confirmDelete = window.confirm(
            "Are you sure you want to delete your profile? This action cannot be undone. If you want to sign up again later, you will need to pay again."
        );
        if (!confirmDelete) {
            trackEvent('profile_delete_cancelled')
            return;
        }

        const url = `/api/delete_profile/${profileid}`;

        fetchData(url).then((data) => {
            if (data.responsecode === 200) {
                trackEvent('profile_delete_success')
                alert("Profile deleted. Thank you for your time with us and we hope you found a companion in your time with us.");
                navigate('/');
            } else {
                alert("Something went wrong. Please contact support.");
            }
        }).catch((error) => {
            console.error("Error deleting profile:", error);
            throw error;
        });
    }

    function logout() {
        trackEvent('logout_clicked')
        Cookies.remove('auth');
        Cookies.remove('email');
        replaceWithDomainHomepage();
    }

    function checkgendermale() {
        if (profiledata.gender1) {
            if (profiledata.gender1 === "Male") {
                return true
            } else {
                return false
            }
        } else {
            return true
        }
    }

    function finishProfileViewTracking(exitReason = 'unmount') {
        const tracking = profileViewTrackingRef.current;
        if (!tracking || tracking.finished) {
            return;
        }
        tracking.finished = true;
        const durationMs = Math.max(Date.now() - tracking.startedAt, 0);
        trackEvent(tracking.eventName, {
            ...tracking.context,
            duration_ms: durationMs,
            duration_seconds: Math.round(durationMs / 1000),
            exit_reason: exitReason,
        });
    }

    React.useEffect(load, []);
    React.useEffect(() => {
        const handlePageHide = () => finishProfileViewTracking('pagehide');
        window.addEventListener('pagehide', handlePageHide);
        return () => {
            window.removeEventListener('pagehide', handlePageHide);
            finishProfileViewTracking('unmount');
        };
    }, []);
    React.useEffect(() => {
        if (props.authenticate === false) {
            return () => {};
        }
        return validateAuthCookie();
    }, [props.authenticate, navigate]);

    return (
            <>
                {(Cookies.get('auth') !== undefined || props.authenticate === false)
                    ? <>
                    <GeneralModal 
                       showModal={individualchatvisible} maintitle={turntoacronym(individualchattitle.fullname1)}
                       notdisableproceed={false} canceltext="Back"
                       setShowModal={setIndividualchatvisible} onClose={()=>console.log('Closed.')}
                       middlebutton={true}
                       middlebuttontext="Call"
                       middlebuttonicon="📞"
                       onMiddleButtonClick={() => {
                           primeCallAudioFromUserGesture();
                           setCallSignal((prev) => prev + 1);
                       }}
                   >
                      <ChatInterface 
                           womandata={womandata}
                           male={individualchattitle}
                           chatdata={[]}
                           callSignal={callSignal}
                       />
                   </GeneralModal>
       
                   <GeneralModal 
                       showModal={showeditprofilemodal} maintitle="Edit Profile"
                       notdisableproceed={false} canceltext="Back"
                       setShowModal={setEditProfileModal} onClose={()=>console.log('Closed.')}
                   >
                       <div className="w-full bg-black p-3 rounded-full">
                           <Signup 
                               allquestions={femaleeditquestions}
                               url="/api/edit_data"
                               mensform={true}
                               allrequired={false}
                               og_data={profiledata}
                               gender="Female"
                               edit={true}
                           />
                       </div>
                   </GeneralModal>
       
                   <div className="ees-profile-dashboard ees-profile-detail-page">
                       <style>{`
                           .ees-profile-detail-page{--ink:#171411;--ink2:#625951;--paper:#f6f1e9;--paper2:#fffaf3;--wine:#3a1a22;--wine2:#512430;--gold:#bd9154;--gold2:#e4c79d;--line:rgba(23,20,17,.10);--shadow:0 18px 55px rgba(37,24,23,.12);position:fixed;inset:0;z-index:20;overflow:auto;width:100vw;min-height:100vh;background:radial-gradient(circle at 100% 0,rgba(189,145,84,.11),transparent 34%),var(--paper);padding:1px 0 92px;font-family:Inter,ui-sans-serif,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;text-transform:none}
                           .ees-profile-detail-card{width:min(720px,calc(100% - 32px));margin:64px auto 96px!important;min-height:calc(100vh - 160px)}
                           .ees-profile-detail-page .ees-mobile-nav{position:fixed;left:50%;bottom:14px;transform:translateX(-50%);width:min(440px,calc(100% - 28px));z-index:35;background:rgba(27,14,18,.94);backdrop-filter:blur(16px);border:1px solid rgba(228,199,157,.26);border-radius:22px;box-shadow:0 18px 45px rgba(27,14,18,.24);display:grid;grid-template-columns:repeat(4,1fr);padding:8px}
                           .ees-profile-detail-page .ees-mobile-nav a,.ees-profile-detail-page .ees-mobile-nav button{border:0;background:transparent;color:rgba(255,255,255,.68);font-size:10px;display:flex;flex-direction:column;align-items:center;gap:4px;padding:7px 3px;border-radius:14px;text-decoration:none}
                           .ees-profile-detail-page .ees-mobile-nav .active{background:rgba(189,145,84,.18);color:#fff}
                           .ees-profile-detail-page .ees-mobile-nav svg{width:18px;height:18px;stroke-width:1.8}
                       `}</style>
                       <div className="ees-profile-detail-card relative bg-gradient-to-b from-[#2b151b] to-[#150b0e] shadow-xl rounded-3xl text-gray-900 flex flex-col justify-center items-center border border-[#bd9154]/30 overflow-hidden">
                       <button
                           className="absolute top-2 left-2 text-white"
                           onClick={() => navigate(-1)}
                           aria-label="Back"
                       >
                           <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                           </svg>
                       </button>
                       <button
                           className="absolute top-2 right-2 text-white"
                           onClick={logout}
                           aria-label="Logout"
                       >
                           <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h6a2 2 0 012 2v1" />
                           </svg>
                       </button>
                       <div className="rounded-t-lg w-1/2 h-auto overflow-hidden flex justify-center">
                            {loading
                                ? <SmartImageComponent preventDownload cssclasses="object-cover object-top w-auto h-32 rounded-full m-1" src={'/assets/media/Portrait_Placeholder.png'} id="Woman looking front" backup={true}/>
                                : <SmartImageComponent preventDownload cssclasses="object-cover object-top w-auto h-32 rounded-full m-1" src={"/api/profilepic/1?EMAIL="+profiledata.EMAIL} id="Woman looking front" backup={true}/>
                            }
                       </div>
                       
       
                       <div className="text-center mt-2">
                           {!checkgendermale()
                               ? <h2 className="font-semibold text-white mb-1">{loading ? <Loader/> : <>{[profiledata.firstname1, profiledata.middlename1, profiledata.lastname1].filter(Boolean).join(' ')}{isVerifiedProfile(profiledata) ? <VerifiedProfileBadge /> : null}</>}</h2>
                               : <h2 className="font-semibold text-white mb-1">{loading ? <Loader/> : <>{turntoacronym(`${profiledata.fullname1}`)}{isVerifiedProfile(profiledata) ? <VerifiedProfileBadge /> : null}</>}</h2>
                           }
                           
                           {!checkgendermale()
                               ? <p className="text-gray-500 mb-1">{loading ? <Loader/> : profiledata.EMAIL}</p>
                               : null
                           }
                           <p className="text-gray-500 mb-1">{loading ? <Loader/> : profiledata.dateofbirth1}</p>
                           <p className="text-gray-500 mb-1">
                                {checkgendermale()
                                    ? <>NATIONALITY: {loading ? <Loader/> : profiledata.ethnicity1}</>
                                    : <>ETHNICITY: {loading ? <Loader/> : profiledata.ethnicity1}</>
                                }
                           </p>
                           <p className="text-gray-500 mb-1">{loading ? <Loader/> : profiledata.zodiacsign}</p>
                           <p className="text-gray-500 mb-1">RELIGION: {loading ? <Loader/> : profiledata.religion1}</p>
                           <p className="text-gray-500 mb-1">OCCUPATION: {loading ? <Loader/> : profiledata.currentoccupation1}</p>
                           <p className="text-gray-500 mb-1">PACKAGE: {loading ? <Loader/> : profiledata.package1}</p>
                       </div>
                       <ul className="py-4 mt-2 text-gray-700 flex grid grid-cols-3 gap-3">
                           {loading
                                ? <Loader/>
                                : <>
                                    {profiledata.age1==="" ? null
                                        : <li className="flex flex-col items-center justify-around">
                                            <svg className="w-4 fill-current text-white" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><path d="M16 15.503A5.041 5.041 0 1 0 16 5.42a5.041 5.041 0 0 0 0 10.083zm0 2.215c-6.703 0-11 3.699-11 5.5v3.363h22v-3.363c0-2.178-4.068-5.5-11-5.5z"/></svg>
                                            <div className="text-white">Age: {loading ? <Loader/> : profiledata.age1}</div>
                                            </li>
                                    }
                                  </>
                           }
                           {loading
                                ? <Loader/>
                                : <>
                                    {profiledata.city1==="" ? null
                                        : <li className="flex flex-col items-center justify-between">
                                            <svg className="w-4 fill-current text-white" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M11 1H1V15H3V11H5V15H15V9H11V1ZM5 3H3V5H5V3ZM9 3H7V5H9V3ZM7 7H9V9H7V7ZM11 11H13V13H11V11Z" />
                                            </svg>
                                            <div className="text-white">{profiledata.city1}</div>
                                        </li>
                                    }
                                  </>
                           }

                            {loading
                                ? <Loader/>
                                : <>
                                    {profiledata.country1==="" ? null
                               : <li className="flex flex-col items-center justify-around">
                                   <svg className="w-4 fill-current text-white" version="1.1" id="_x32_" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" 
                                       viewBox="0 0 512 512"  xmlSpace="preserve">
                                   <g>
                                       <path className="st0" d="M454.111,193.473c-3.617,3.21-7.4,6.252-11.391,9.095c-9.236,6.546-14.26,10.143-17.404,12.866
                                           c9.216,20.58,14.875,43.094,15.996,66.857h-66.418h-17.61h-92.449V170.384h-17.671h-70.307c2.769-6.606,5.732-12.886,8.915-18.731
                                           c9.522-17.564,20.753-31.484,32.724-40.786c0.901-0.7,1.802-1.321,2.703-1.975c5.479-1.028,11.064-1.749,16.696-2.296
                                           c-0.327-3.737-0.554-7.487-0.554-11.257c0-8.348,0.814-16.503,2.322-24.417c-54.439,3.977-103.533,27.693-139.848,64.022
                                           c-39.939,39.919-64.702,95.252-64.689,156.184c-0.014,60.932,24.75,116.265,64.689,156.184
                                           c39.912,39.939,95.245,64.703,156.184,64.689c60.939,0.014,116.266-24.75,156.184-64.689
                                           c39.938-39.918,64.695-95.251,64.689-156.184C476.879,256.1,468.658,222.935,454.111,193.473z M124.813,159.941
                                           c17.27-17.264,37.903-31.09,60.812-40.492c-10.918,14.133-20.213,31.404-27.76,50.936h-42.661
                                           C118.287,166.794,121.463,163.284,124.813,159.941z M101.731,188.054h49.955c-8.708,28.134-13.86,60.165-14.634,94.238H70.688
                                           C72.322,247.478,83.52,215.248,101.731,188.054z M94.504,382.422c-13.92-24.55-22.408-52.538-23.816-82.46h66.424
                                           c0.674,29.435,4.624,57.275,11.317,82.46H94.504z M124.813,422.314c-6.886-6.887-13.2-14.334-18.938-22.221h47.833
                                           c4.691,14.053,10.17,27.193,16.529,38.931c4.711,8.675,9.876,16.616,15.422,23.803
                                           C162.737,453.424,142.097,439.584,124.813,422.314z M247.164,476.433c-8.835-0.414-17.496-1.475-25.958-3.07
                                           c-0.908-0.654-1.808-1.274-2.71-1.968c-17.971-13.948-34.206-38.357-45.664-69.701c-0.186-0.52-0.353-1.081-0.547-1.602h74.879
                                           V476.433z M247.164,382.422H166.68c-7-24.758-11.251-52.725-11.965-82.46h92.449V382.422z M247.164,282.292h-92.509
                                           c0.814-34.54,6.453-66.744,15.568-94.238h76.94V282.292z M264.835,299.963h92.509c-0.701,29.729-4.985,57.702-11.985,82.46h-80.524
                                           V299.963z M293.503,471.395c-0.894,0.694-1.802,1.314-2.71,1.968c-8.462,1.595-17.123,2.656-25.958,3.07v-76.34h74.898
                                           c-3.964,11.044-8.488,21.28-13.506,30.509C316.705,448.165,305.474,462.079,293.503,471.395z M387.193,422.314
                                           c-17.283,17.264-37.91,31.097-60.825,40.506c11.691-15.135,21.574-33.792,29.388-55.04c0.921-2.496,1.749-5.112,2.603-7.688h47.766
                                           C400.385,407.98,394.073,415.427,387.193,422.314z M417.495,382.422H363.69c6.673-25.165,10.584-53.051,11.257-82.46h66.365
                                           C439.904,329.885,431.409,357.872,417.495,382.422z"/>
                                       <path className="st0" d="M313.762,173.741c22.468,15.949,27.434,19.746,35.341,34.833c6.253,11.945,16.39,32.885,16.39,32.885
                                           c0.48,0.954,1.448,1.562,2.522,1.562c1.061,0,2.049-0.608,2.522-1.562c0,0,10.143-20.94,16.396-32.885
                                           c7.894-15.088,12.866-18.884,35.341-34.833c24.63-17.47,41.086-45.911,41.086-78.402C463.359,42.688,420.665,0,368.014,0
                                           c-52.658,0-95.332,42.688-95.332,95.339C272.682,127.83,289.125,156.27,313.762,173.741z M368.014,55.013
                                           c22.275,0,40.332,18.058,40.332,40.326c0,22.274-18.057,40.332-40.332,40.332c-22.275,0-40.326-18.058-40.326-40.332
                                           C327.689,73.071,345.739,55.013,368.014,55.013z"/>
                                   </g>
                                   </svg>
                                   <div className="text-white">{profiledata.country1}</div>
                                 </li>
                           }
                                  </>
                           }
                           
                           
                       </ul>
       
                       <ReactMarkdown className="text-white p-4 normal-case">{profiledata.futurespouse1 || ''}</ReactMarkdown>
       
                       <div className="p-4 border-t mx-8 mt-2">
                           {checkgendermale()
                               ? <button className="w-full block mx-auto rounded-full bg-blue-300 hover:shadow-lg font-semibold text-black px-6 py-2" onClick={()=>messageManFunction()}>Message</button>
                               : null
                           }
                           
                       </div>
                       {checkgendermale() ? null
                               : <>
                                   <button className="w-full block mx-auto rounded-full bg-gray-100 hover:shadow-lg font-semibold text-black px-6 py-2" onClick={()=>editManFunction()}>Edit</button>
                                    <button className="w-full block mx-auto rounded-full bg-gray-900 hover:shadow-lg font-semibold text-white px-6 py-2" onClick={()=>setEditimages(true)}>Edit Images</button>
                                    <button className="w-full block mx-auto rounded-full bg-yellow-600 hover:shadow-lg font-semibold text-black px-6 py-2 mt-4" onClick={logout}>Log out</button>
                                    <button className="w-full block mx-auto rounded-full bg-red-700 hover:shadow-lg font-semibold text-white px-6 py-2 mt-4" onClick={()=>deleteProfileFunction()}>Delete Profile</button>
       
                                    {showeditimages
                                        ? <div className="w-full flex flex-col justify-center items-center">
                                            <div className="rounded-t-lg w-1/3 h-auto overflow-hidden">
                                               {loading ? <Loader/> : <SmartImageComponent preventDownload cssclasses="object-cover object-top w-full rounded-full m-1" src={"/api/profilepic/1?EMAIL="+profiledata.EMAIL} id="Woman looking front" backup={true}/>}

                                                <p>Image</p>
                                                {loading ? <Loader/> : <ImageFormComponent EMAIL={profiledata.EMAIL} id={generateUUID()} fullname1={profiledata.fullname1} allanswers={profiledata}/>}
                                            </div>
                                           </div>
                                        : null
                                    }
                                 </>
                           }
                       </div>
                       <nav className="ees-mobile-nav">
                           <Link to={'/personalprofile/'+(searchParams.get('EMAIL') || profileid)+'#matches'}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z"/></svg>Matches</Link>
                           <button type="button" onClick={messageManFunction}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z"/></svg>Message</button>
                           <Link className="active" to={'/profile/'+profileid}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>Profile</Link>
                           <button type="button" onClick={() => navigate(-1)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="m15 18-6-6 6-6"/></svg>Back</button>
                       </nav>
                   </div>
                   </>
                   : <Navigate to="/" replace />
                }
            </>
        )
}

export function LoginCheckComponent(props) {
        return (
            <>
                <AuthCookieGate>{props.thecomponent}</AuthCookieGate>
            </>
        )
}

export function LoginCheckComponentGeneral(props) {
    return (
        <>
            <AuthCookieGate>{props.thecomponent}</AuthCookieGate>
        </>
    )
}

export function AdminCheckComponent(props) {
    return (
        <>
            <AuthCookieGate adminOnly={true}><AdminLayout>{props.thecomponent}</AdminLayout></AuthCookieGate>
        </>
    )
}

function ProfileCard(props) {
    const profiledata = props.profiledata
    const [daterequested, setDateRequested] = React.useState(false)
    const [loading, setLoading] = React.useState(false)

    function messageManFunction() {
        props.setIndividualChatTitle(profiledata)
        props.setIndividualchatvisible(true)
    }

    function dateonlyrequestman() {
        setLoading(true)
        const url = `/api/requestdate?EMAIL=${props.heremail}&guy=${profiledata.fullname1}`;

        fetchData(url).then((data) => {
            console.log(data)
            if (data.responsecode === 200) {
                setDateRequested(true)
                alert("You have successfully requested a date with this bachelor(ette). If they accept you will recieve an email with the next steps.")
            } else if (data.responsecode === 301) {
                alert("You have already requested a date with this bachelor.")
            } else {
                alert("Something went wrong. Please contact support.")
            }
            setLoading(false)
        }).catch((error) => {
            console.error("Error fetching purgatory data:", error);
            throw error;
        });
    }

    if (props.dashboard) {
        return (
            <article className="ees-profile-card" key={props.key}>
                <div className="ees-cardtop">
                    <div className="ees-person">
                        <div className="ees-personavatar">
                            <SmartImageComponent
                                preventDownload
                                cssclasses="ees-personimg"
                                src={"/api/profilepic/1?EMAIL=" + profiledata.EMAIL}
                                backupurl={"/api/profilepic/1?EMAIL=" + profiledata.EMAIL}
                                id={"Pic of:" + profiledata.fullname1}
                                backup={true}
                            />
                        </div>
                        <div>
                            <h4>{turntoacronym(profiledata.fullname1)}{isVerifiedProfile(profiledata) ? <VerifiedProfileBadge /> : null}</h4>
                            <p>{profiledata.country1 || ""}{profiledata.age1 ? ` · ${profiledata.age1}` : ""}{profiledata.occupation1 ? ` · ${profiledata.occupation1}` : ""}</p>
                        </div>
                    </div>
                    {profiledata.package1 ? <span className="ees-package-pill">{profiledata.package1}</span> : null}
                </div>
                <div className="ees-card-actions">
                    <Link className="ees-view" to={'/profile/'+Base64.encode(profiledata.fullname1)+"?id="+props.herid+"&EMAIL="+Base64.encode(props.heremail)}>View profile</Link>
                    <button className="ees-message" onClick={()=>messageManFunction()}>Send message</button>
                </div>
                {props.herpackage === "Ruby"
                    ? <button className="ees-offline" onClick={()=>dateonlyrequestman()}>
                        {loading ? "Loading" : daterequested ? "Requested" : "Let's take it offline"}
                      </button>
                    : null
                }
            </article>
        )
    }

    return (
        <div className="w-11/12 md:w-1/2 mt-2 bg-gray-900 shadow-xl rounded-lg text-gray-900 mx-6" key={props.key}>
                <div className="rounded-t-lg h-32 w-auto overflow-hidden flex justify-center items-center mt-3">
                    <SmartImageComponent preventDownload cssclasses="object-cover object-top h-full w-auto rounded-full m-1" src={"/api/profilepic/1?EMAIL="+profiledata.EMAIL} backupurl={"/api/profilepic/1?EMAIL="+profiledata.EMAIL} id={"Pic of:" + profiledata.fullname1} backup={true}/>
                </div>
                <div className="text-center mt-2">
                    <h2 className="font-semibold text-white mb-1">{turntoacronym(profiledata.fullname1)}{isVerifiedProfile(profiledata) ? <VerifiedProfileBadge /> : null}</h2>
                    
                    <p className="text-gray-500 mb-1">{profiledata.dateofbirth1}</p>
                    <p className="text-gray-500 mb-1">{profiledata.ethnicity1}</p>
                    <p className="text-gray-500 mb-1">{profiledata.zodiacsign}</p>
                    {profiledata.religion1 !== ""
                        ? <p className="text-gray-500 mb-1">RELIGION: {profiledata.religion1}</p>
                        : null
                    }
                </div>
                <ul className="py-3 mt-1 text-gray-700 flex flex-col items-center justify-around">
                </ul>
                <div className="p-3 border-t mx-8 mt-1">
                        <button className="w-full block mx-auto rounded-full bg-blue-300 hover:shadow-lg font-semibold text-black px-6 py-2 text-center" onClick={()=>messageManFunction()}>Message</button>
                        <Link className="w-full block mx-auto rounded-full bg-white hover:shadow-lg font-semibold text-black px-6 py-2 text-center mt-1 text-sm" to={'/profile/'+Base64.encode(profiledata.fullname1)+"?id="+props.herid+"&EMAIL="+Base64.encode(props.heremail)}>Visit Profile</Link>
                        {props.herpackage === "Ruby" 
                            ? <button className="w-full block mx-auto rounded-full bg-gray-100 hover:shadow-lg font-semibold text-black px-6 py-2 text-center mt-1 text-sm" onClick={()=>dateonlyrequestman()}>
                                {loading ? "Loading" : daterequested ? "Requested" : "Let's take it offline"}
                            </button> 
                            : null
                        }
                </div>
            </div>
    )
}

export function GeneralModal(props){
    return (
      <>
        {props.showModal ? (
          <>
            <style>{`
              .ees-modal-shell{--ink:#171411;--ink2:#625951;--paper:#f6f1e9;--paper2:#fffaf3;--wine:#3a1a22;--wine2:#512430;--gold:#bd9154;--gold2:#e4c79d;--line:rgba(23,20,17,.10);--shadow:0 24px 70px rgba(27,14,18,.24);--serif:Georgia,"Times New Roman",serif;--sans:Inter,ui-sans-serif,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-family:var(--sans);text-transform:none;color:var(--ink)}
              .ees-modal-card{max-height:100%;overflow:hidden;border:1px solid rgba(189,145,84,.22);border-radius:26px;box-shadow:var(--shadow);position:relative;display:flex;flex-direction:column;width:100%;background:radial-gradient(circle at 10% 0,rgba(228,199,157,.24),transparent 32%),var(--paper2);outline:none}
              .ees-modal-card:before{content:"";position:absolute;left:0;right:0;top:0;height:4px;background:linear-gradient(90deg,var(--wine),var(--gold2),var(--wine))}
              .ees-modal-head{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;gap:12px;padding:18px 20px;border-bottom:1px solid var(--line)}
              .ees-modal-title{justify-self:start;margin:0;font-family:var(--serif);font-size:clamp(22px,4vw,32px);font-weight:600;color:var(--wine);letter-spacing:-.02em}
              .ees-modal-body{position:relative;padding:18px;flex:auto;overflow-y:auto;background:linear-gradient(180deg,rgba(255,250,243,.72),rgba(246,241,233,.55))}
              .ees-modal-foot{display:flex;align-items:center;justify-content:flex-end;gap:10px;padding:14px 18px;border-top:1px solid var(--line);background:rgba(255,250,243,.88)}
              .ees-modal-close,.ees-modal-action,.ees-modal-link{border:1px solid rgba(189,145,84,.36);border-radius:999px;background:#fffaf3;color:var(--wine);font-weight:700;letter-spacing:.08em;text-transform:uppercase;font-size:12px;padding:10px 15px;line-height:1;box-shadow:0 8px 20px rgba(37,24,23,.08)}
              .ees-modal-close-x{justify-self:end;width:36px;height:36px;border-radius:50%;border:1px solid rgba(189,145,84,.3);background:#fffaf3;color:var(--wine);font-size:22px;line-height:1;display:grid;place-items:center}
              .ees-modal-action{background:linear-gradient(135deg,var(--wine2),var(--wine));color:#fff;border-color:transparent}
              .ees-modal-link{justify-self:end;background:linear-gradient(135deg,var(--gold2),var(--gold));color:#28151a;text-decoration:none}
              .ees-modal-list{display:flex;flex-direction:column;gap:12px;width:100%}
              .ees-modal-row{width:100%;display:flex;align-items:center;gap:12px;padding:14px;border-radius:18px;border:1px solid rgba(189,145,84,.20);background:rgba(255,250,243,.86);color:var(--ink);box-shadow:0 10px 28px rgba(37,24,23,.07);text-align:left}
              .ees-modal-row:hover{border-color:rgba(189,145,84,.48);transform:translateY(-1px)}
              .ees-modal-row img{border:2px solid rgba(189,145,84,.35)}
              .ees-modal-row-main{flex:1;min-width:0;color:var(--ink);font-weight:650}
              .ees-modal-row-meta{margin-top:4px;color:var(--ink2);font-size:12px}
              @media(max-width:640px){.ees-modal-wrap{height:calc(100% - 1.25rem);margin:.625rem;width:calc(100% - 1.25rem)}.ees-modal-card{border-radius:22px}.ees-modal-head{padding:16px}.ees-modal-body{padding:14px}.ees-modal-title{font-size:24px}}
            `}</style>
            <div className="ees-modal-shell justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
              <div className="ees-modal-wrap h-[calc(100%-3rem)] relative w-full my-6 mx-auto max-w-3xl">
                <div className="ees-modal-card">
                  <div className="ees-modal-head">
                    <h3 className="ees-modal-title">
                      {props.maintitle}
                    </h3>
                    {props.middlebutton
                        ? <button
                            className="ees-modal-action"
                            onClick={props.onMiddleButtonClick}
                          >
                            <span className="mr-1" aria-hidden="true">{props.middlebuttonicon || "📞"}</span>
                            {props.middlebuttontext || "Call"}
                          </button>
                        : <div />
                    }
                    {props.lilbutton
                        ? <Link className="ees-modal-link" to={props.lilbuttonlink}>
                            {props.lilbuttontext}
                          </Link>
                        : props.dismissible !== false ? <button
                            className="ees-modal-close-x"
                            onClick={() => {
                                props.setShowModal(false)
                                if (props.onClose) props.onClose()
                            }}
                            aria-label="Close"
                          >
                            ×
                          </button> : null
                    }
                  </div>
                  <div className="ees-modal-body">
                    {props.children}
                  </div>
                  <div className="ees-modal-foot">
                    {props.dismissible !== false && <button
                      className="ees-modal-close"
                      type="button"
                      onClick={() => {
                        props.setShowModal(false)
                        if (props.onClose) props.onClose()
                      }}
                    >
                      {props.canceltext}
                    </button>}
                    {props.notdisableproceed && props.dismissible !== false
                      ? <SubmitButton
                          type="button"
                          onClick={() => {props.setShowModal(false)}}
                        >
                          {props.submittext}
                        </SubmitButton> 
                      : null
                    }
                  </div>
                </div>
              </div>
            </div>
            <div className="opacity-60 fixed inset-0 z-40 bg-black"></div>
          </>
        ) : null}
      </>
    );
}

function isCallInitiatedMessage(text) {
    if (typeof text !== "string") {
        return false;
    }

    const normalizedText = text.replace(/\s+/g, " ").trim();
    return /^\[\[\s*---- CALL INITIATED ----\s*\]\]$/i.test(normalizedText);
}

function NotificationText({ text }) {
    if (!isCallInitiatedMessage(text)) {
        return <p className="text-black text-base">{text}</p>;
    }

    return (
        <div className="flex items-center space-x-2 text-black text-base">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h2.28a2 2 0 011.897 1.368l1.02 3.06a2 2 0 01-.454 2.11l-1.2 1.2a16 16 0 006.364 6.364l1.2-1.2a2 2 0 012.11-.454l3.06 1.02A2 2 0 0121 16.72V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <span>Missed call</span>
        </div>
    );
}

export function OrientationModal({show, placeholder, isLast, onNext, children}) {
    return (
      <>
        {show ? (
          <>
            <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
              <div className="h-[calc(100%-3rem)] relative w-auto my-6 mx-auto max-w-3xl">
                <div className="max-h-full overflow-hidden border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                  <div className="p-3 border-b border-solid border-blueGray-200 rounded-t">
                    <h3 className="text-3xl font-semibold">Orientation</h3>
                  </div>
                  <div className="relative p-4 flex-auto overflow-y-auto">
                    <p className="text-black mb-2">{placeholder}</p>
                    {children}
                  </div>
                  <div className="flex items-center justify-end p-4 border-t border-solid border-blueGray-200 rounded-b">
                    <button
                      className="bg-black text-white font-bold uppercase px-6 py-2 text-sm rounded"
                      type="button"
                      onClick={onNext}
                    >
                      {isLast ? 'Close' : 'Next'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="opacity-50 fixed inset-0 z-40 bg-black"></div>
          </>
        ) : null}
      </>
    );
}

const PROFILE_LOAD_BATCH_SIZE = 15;

function getProfileIdentity(item) {
    return item.__id || item.EMAIL || item.fullname1 || '';
}

function IndividualSection({section, men, minage, maxage, country, mainpackage, relationshipstatus, setIndividualChatTitle, setIndividualchatvisible, herid, heremail, herpackage, hiddenProfiles, dashboard = false, searchTerm = "", batchSize = PROFILE_LOAD_BATCH_SIZE, hasMoreProfiles = false, onLoadMoreProfiles = null}) {
    const sectionclasses = {
        "All Men": "bg-gradient-to-r from-yellow-400 to-pink-600", 
        "Your Matches": "bg-gradient-to-r from-pink-500 to-rose-500", 
        "Loading": "bg-gradient-to-r from-grey-100 to-grey-400",
    }

    const [showsection, setShowsection] = React.useState(true)
    const [visibleCount, setVisibleCount] = React.useState(batchSize);
    const [loadingMoreProfiles, setLoadingMoreProfiles] = React.useState(false);
    const maxVisibleCount = section === "All Men" ? 40 : Infinity;
    const loadMoreInFlight = React.useRef(false);
    const loadMoreButtonRef = React.useRef(null);
    
    function checkitem(item) {
        var maxagematches = true
        var minagematches = true
        var countrymatches = true
        var packagematches = true
        var statusmatches = true

        if (minage!=="") {
            if (!(Number(item["age1"]) >= Number(minage))) {
                console.log("Age not in filter")
                minagematches = false
            }
        } 

        if (maxage!=="") {
            if (!(Number(item["age1"]) <= Number(maxage))) {
                console.log("Age not in filter")
                maxagematches = false
            }
        }

        if (country !== "") {
            const guyCountry = item["country1"]?.trim()?.toLowerCase();
            const herCountry = country.trim().toLowerCase();
        
            console.log("The guy's country:", guyCountry);
            console.log("Her country:", herCountry);
            console.log("Guy's country char codes:", [...item['country1']].map(c => c.charCodeAt(0)));
            console.log("Her country char codes:", [...country].map(c => c.charCodeAt(0)));

            if (guyCountry !== herCountry) {
                countrymatches = false;
            }
        }
        

        if (mainpackage!=="") {
            if (!(item["package1"] === mainpackage)) {
                packagematches = false
            }
        }

        if (item["age1"] === "") {
            minagematches = false
        }

        if (item['gender1'] === "Female" || item["EMAIL"].includes("@gmail.com") || item["EMAIL"].includes("@yahoo.com") || item["EMAIL"].includes("@outlook.com")) {
            console.log('This is a woman must be removed.')
            minagematches = false
        }
        
        console.log("The guys name:", item['fullname1'])
        console.log("The guys age:", item['age1'])
        console.log("After the check.")
        console.log("Minage:", minagematches)
        console.log("Maxage:", maxagematches)
        console.log("Country:", countrymatches)
        console.log("Relationship Status:", statusmatches)
        
        if (maxagematches === true && minagematches === true && countrymatches === true && statusmatches === true && packagematches === true) {
            return true
        } else {
            return false
        }
    }

    function getHiddenProfileNames() {
        if (Array.isArray(hiddenProfiles)) {
            return hiddenProfiles
        }

        if (typeof hiddenProfiles === 'string' && hiddenProfiles.trim() !== '') {
            try {
                const parsedHiddenProfiles = JSON.parse(hiddenProfiles)
                if (Array.isArray(parsedHiddenProfiles)) {
                    return parsedHiddenProfiles
                }
            } catch (err) {
                console.warn('Could not parse hidden_profiles for IndividualSection:', err)
            }
        }

        return []
    }

    function isProfileHiddenForUser(item, hiddenProfilesValue) {
        const profileName = (item.fullname1 || '').trim().toLowerCase()
        if (!profileName) {
            return false
        }

        const hiddenNames = Array.isArray(hiddenProfilesValue) ? hiddenProfilesValue : getHiddenProfileNames()
        return hiddenNames.some((hiddenName) => String(hiddenName || '').trim().toLowerCase() === profileName)
    }

    const hiddenProfileNames = React.useMemo(() => getHiddenProfileNames(), [hiddenProfiles])

    const sortedMen = React.useMemo(() => {
        const normalizePackage = (value) => (value || '').trim().toLowerCase();
        const herPackageValue = normalizePackage(herpackage);
        const packageOrder = packages.map((item) => item.toLowerCase());

        const getPackageRank = (value) => {
            const normalizedValue = normalizePackage(value);
            if (normalizedValue && normalizedValue === herPackageValue) {
                return -1;
            }
            const index = packageOrder.indexOf(normalizedValue);
            return index === -1 ? packageOrder.length : index;
        };

        return [...men].sort((a, b) => {
            const rankA = getPackageRank(a.package1);
            const rankB = getPackageRank(b.package1);
            if (rankA !== rankB) {
                return rankA - rankB;
            }
            return Number(b.timestamp) - Number(a.timestamp);
        });
    }, [men, herpackage]);

    const filteredMen = React.useMemo(
        () => sortedMen.filter((item) => {
            const query = String(searchTerm || '').trim().toLowerCase();
            const profileText = [item.fullname1, item.country1, item.occupation1, item.package1].join(' ').toLowerCase();
            const searchMatches = query === '' || profileText.includes(query);
            return searchMatches && !Object.prototype.hasOwnProperty.call(item, 'ramzy') && !isProfileHiddenForUser(item, hiddenProfileNames) && checkitem(item);
        }),
        [sortedMen, hiddenProfileNames, minage, maxage, country, mainpackage, relationshipstatus, searchTerm]
    );

    const effectiveVisibleCount = Math.min(visibleCount, maxVisibleCount);

    const displayedMen = React.useMemo(
        () => filteredMen.slice(0, effectiveVisibleCount),
        [filteredMen, effectiveVisibleCount]
    );

    const hasMore = (filteredMen.length > effectiveVisibleCount || hasMoreProfiles) && effectiveVisibleCount < maxVisibleCount;

    function loadMoreProfiles() {
        if (loadMoreInFlight.current) {
            return;
        }
        loadMoreInFlight.current = true;
        setVisibleCount((prev) => Math.min(prev + batchSize, maxVisibleCount));
        if (onLoadMoreProfiles) {
            setLoadingMoreProfiles(true);
            Promise.resolve(onLoadMoreProfiles()).finally(() => {
                loadMoreInFlight.current = false;
                setLoadingMoreProfiles(false);
            });
        } else {
            loadMoreInFlight.current = false;
        }
    }

    React.useEffect(() => {
        setVisibleCount(batchSize);
    }, [batchSize, minage, maxage, country, mainpackage, relationshipstatus, hiddenProfileNames, searchTerm]);

    React.useEffect(() => {
        const loadMoreButton = loadMoreButtonRef.current;
        if (!loadMoreButton || !hasMore || loadingMoreProfiles) {
            return undefined;
        }

        const observer = new IntersectionObserver((entries) => {
            const [entry] = entries;
            if (entry.isIntersecting) {
                loadMoreProfiles();
            }
        }, { rootMargin: '160px 0px' });

        observer.observe(loadMoreButton);
        return () => observer.disconnect();
    }, [hasMore, loadingMoreProfiles, displayedMen.length, effectiveVisibleCount, filteredMen.length, hasMoreProfiles]);

    if (dashboard) {
        return (
            <>
                {displayedMen.map(function(item, index){
                    return (
                        <ProfileCard
                            dashboard
                            profiledata={item}
                            setIndividualChatTitle={setIndividualChatTitle}
                            setIndividualchatvisible={setIndividualchatvisible}
                            herid={herid}
                            heremail={heremail}
                            key={getProfileIdentity(item) || index}
                            herpackage={herpackage}
                        />
                    )
                })}
                {hasMore
                    ? <button
                        ref={loadMoreButtonRef}
                        className="ees-load-more"
                        onClick={loadMoreProfiles}
                        disabled={loadingMoreProfiles}
                        aria-busy={loadingMoreProfiles}
                        aria-label={loadingMoreProfiles ? "Loading more profiles" : "Load more profiles"}
                    >
                        {loadingMoreProfiles
                            ? <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                            : 'Load more'
                        }
                    </button>
                    : null
                }
            </>
        )
    }

    return (
        <div className={"flex flex-col px-10 py-4 "+sectionclasses[section]}>
            <div className="grid grid-cols-2">
                <p className="text-xl text-white">{section}</p>
                {showsection
                    ? <button className="w-full mx-auto rounded-full bg-black hover:shadow-lg font-semibold text-white px-6 py-2" onClick={() => setShowsection(false)}><i className="fa-solid fa-eye-slash"></i></button>
                    : <button className="w-full mx-auto rounded-full bg-white hover:shadow-lg font-semibold text-black px-6 py-2" onClick={() => setShowsection(true)}><i className="fa-solid fa-eye"></i></button>
                }
            </div>
            {showsection
                ? <div className="flex flex-col overflow-x-auto justify-center items-center">
                    {displayedMen.map(function(item, index){
                        return (
                            <ProfileCard
                                profiledata={item}
                                setIndividualChatTitle={setIndividualChatTitle}
                                setIndividualchatvisible={setIndividualchatvisible}
                                herid={herid}
                                heremail={heremail}
                                key={getProfileIdentity(item) || index}
                                herpackage={herpackage}
                            />
                        )
                    })}
                        {hasMore
                        ? <button
                            ref={loadMoreButtonRef}
                            className="mt-4 px-6 py-2 rounded-full bg-white text-black font-semibold"
                            onClick={loadMoreProfiles}
                            disabled={loadingMoreProfiles}
                            aria-busy={loadingMoreProfiles}
                            aria-label={loadingMoreProfiles ? "Loading more profiles" : "Load more profiles"}
                        >
                            {loadingMoreProfiles
                                ? <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                                : 'Load more'
                            }
                        </button>
                        : null
                    }
                  </div>
                : null
            }
        </div>
    )
}

export function Loader() {
    return (
        <div role="status">
            <svg aria-hidden="true" className="w-8 h-8 text-white animate-spin dark:text-gray-600 fill-gray-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
            </svg>
            <span className="sr-only">Loading...</span>
        </div>
    )
}

export function PersonalProfile(props) {
    const { profileid } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const siteDomain = getSiteDomain();
    const hostname = typeof window !== 'undefined' ? (window.location.hostname || '') : '';
    const isUpendoHalisi = /(^|\.)upendo-halisi\.com$/i.test(hostname);
    const dashboardClassName = isUpendoHalisi ? "ees-profile-dashboard uh-profile-dashboard" : "ees-profile-dashboard";
    const dashboardBrand = isUpendoHalisi
        ? { mark: 'U·H', firstLine: 'Upendo', secondLine: 'Halisi', memberLabel: 'Upendo Halisi member', introductionsTitle: 'Genuine connections' }
        : { mark: 'E·S', firstLine: 'Expat Elite', secondLine: 'Singles', memberLabel: 'Expat Elite member', introductionsTitle: 'Curated introductions' };

    // Basic Loading
    const [profiledata, setProfileData] = React.useState({})
    const [mendata, setMenData] = React.useState([])
    const [hasMoreMenProfiles, setHasMoreMenProfiles] = React.useState(false)
    const loadedMenOffsetRef = React.useRef(0)
    const menRequestInFlightRef = React.useRef(false)
    const [loading, setLoading] = React.useState(true)
    const [menloading, setMenLoading] = React.useState(true)

    // Chat loading 
    const [chatdata, setChatData] = React.useState([])
    const [chatloading, setChatLoading] = React.useState(true)

    // Modal related
    const [notifmodalvisible, setNotifModalVisible] = React.useState(false)
    const [chatsvisible, setChatsVisible] = React.useState(false)
    const [livenessModalVisible, setLivenessModalVisible] = React.useState(false)
    const [individualchatvisible, setIndividualchatvisible] = React.useState(false)
    const [callSignal, setCallSignal] = React.useState(0)
    const [individualchattitle, setIndividualChatTitle] = React.useState({})
    
    // Filters
    const [minage, setMinAge] = React.useState("")
    const [mainpackage, setMainPackage] = React.useState("")
    const [country, setCountry] = React.useState("")
    const [relationshipstatus, setRelationshipStatus] = React.useState("")
    const [maxage, setMaxAge] = React.useState("")
    const [showfilters, setShowFilters] = React.useState(false)
    const [searchTerm, setSearchTerm] = React.useState("")
    const [orientation, setOrientation] = React.useState(false)
    const [orientationStep, setOrientationStep] = React.useState(0)
    const [dosBlocked, setDosBlocked] = React.useState(false)
    const orientationKey = `orientation_${profileid}`

    const packageVideos = {
        BRONZE: BronzeVideo,
        SILVER: SilverVideo,
        GOLD: GoldVideo,
        PLATINUM: PlatinumVideo,
        DIAMOND: DiamondVideo,
        EMERALD: EmeraldVideo,
    };

    const matchPercentageText = `Understanding Your Match Percentage

At ${isUpendoHalisi ? 'Upendo Halisi' : 'Expat Elite Singles'}, our matchmaking algorithm generates a Match Percentage based on how you and your potential partner responded to the  application form. This percentage indicates how compatible you are in terms of values, lifestyle, goals, and relationship expectations.

Here’s how to interpret your match score:

40 - 50%: Not a Good Match

A connection at this level suggests fundamental differences in values, life goals, or personalities. Pursuing this match is unlikely to lead to a fulfilling relationship.

50 - 60%: Casual Connection

This match may lead to some fun conversations and short-term attraction, but long-term compatibility is weak. While there may be physical chemistry, emotional or intellectual compatibility is likely lacking. This is not an ideal match for a serious relationship.

60 - 70%: Good for Dating, Not for Marriage

There is a solid foundation for a relationship here, with shared interests and mutual respect. However, significant differences in key areas such as life goals, family values, or long-term commitment may make marriage and raising a family together challenging.

70 - 80%: Strong Potential for a Long-Term Relationship

At this level, you are well-matched in terms of values, communication, and chemistry. If you are looking for a serious relationship, this person could be "husband material" with good emotional and intellectual compatibility. However, a long-term commitment such as marriage may still require more effort and compromise.

80 - 90%: Highly Compatible – Great Relationship & Marriage Potential

This is an excellent match. You and your potential partner share deep emotional, intellectual, and lifestyle compatibility. There is good chemistry, strong rapport, and shared long-term goals. This person is not just husband material, but someone with whom you can build a meaningful and lasting relationship.

90 - 100%: Rare, Exceptional Match – A Blissful Connection

Matches at this level are rare but incredibly special. This person is highly compatible with you on all levels, values, lifestyle, emotional connection, and physical chemistry. There is a natural ease in communication and a shared vision for the future. If you find someone at this level, cherish and invest in the relationship, this is a rare and beautiful connection.

    By understanding your match percentage, you can make informed decisions about who to pursue and build meaningful relationships with. Keep in mind that while compatibility is important, building a successful relationship still requires effort, understanding, and commitment from both partners.`

    const termsOfUse = `TERMS OF USE

By accessing, signing up and using ${siteDomain} you acknowledge and agree to these terms of use.

These terms of use, along with our community guidelines, privacy policy and cookie policy define the general conditions that apply to your use of the ${siteDomain} website and any related services.

YOUR COMMITMENT TO US

You assert that you are at least 18 years of age and that you agree to adhere to our community guidelines. Any breach of these requirements may result in the temporary suspension of your user account as later explained or the removal of your user account from ${siteDomain} at our sole discretion.

You agree that no person on the ${siteDomain} website can be forced to communicate with you no engage with you and that ${siteDomain} bears no liability in respect of the same and that you will not query about delayed responses, the inability to see your messages or the like.

You are only able to send messages to  person's on packages similar to yours or below yours. To respond and communicate with person's on packages above yours you would need to upgrade.

You are able to interact with the number of person's on your package, if you continue to try and communicate with person's in excess of what your package you would need to upgrade.

Matches are generated by the system based on your spouse preferences and the answers you provide in the Q&A.

Matches may change with each upgrade or after each upgrade or during the course of your using the service as other users change their preferences.

The system picks matches that are a 80 - 95% match based on the package objective.

Matches that are over 90% are extremely rare.

Matches are not based on what package you are on or where you live. Through the system takes into consideration your preferred location it is not the primary factor in picking matches. They are based on preferences and your questionnaire answers irrespective of the package you are on or where you live.

IMPORTANT NOTE:

Rules of the operations of accounts are as specified in promotional material. The specifics of the same will be outlined and clarified for you by your 24/7 WhatsApp Customer Service Team agent when you access your account and periodically as you proceed.

OUR COMMITMENT TO YOU
${siteDomain} is provided on an as-is basis without any guarantees or warranties of any kind. We will aim to provide a high-quality service, including ensuring that technical uptime is kept to a maximum, data is regularly backed up, and that your enquiries are replied to promptly. We will also aim to resolve any concerns and complaints that you may have to your satisfaction within 24 hours of the report of a complaint.

COMMUNICATING WITH OTHER USERS

We expect you to conduct yourself in a responsible and respectful way when communicating with other users through the website. Never send money or disclose personally identifiable information, such as your home address or telephone number, to people that you do not know. Any such communications are at your own sole discretion and not the responsibility of ${siteDomain}

Your account is valid for a period stated on your package.

If you agree to meet someone in person, you are advised to choose a public location and take appropriate precautions. Any such meetings are at your own sole risk and discretion and not the responsibility of ${siteDomain}.

Please before you agree to meet anyone please verify their identity as best as possible.

WEBSITE CONTENT:

Any data you post on ${siteDomain} including your profile text and photos, become our property in law and we do not use your data except for matching. All other website content, including text, images and code are the property of ${siteDomain} and protected by local and international copyright laws.

We are not RESPONSIBLE or LIABLE for content by users or third parties obtained from our website.

CANCELING YOUR ACCOUNT:

You have the right to terminate your user account with ${siteDomain} at any time with no REFUND PAYABLE IN ANY CIRCUMSTANCES WHATSOEVER.

We reserve the right to limit or remove user accounts that we deem to engage in behavior that runs contrary to the benefit of the ${siteDomain} community as a whole or that consume an unreasonable amount of website resources WITHOUT NOTICE TO THE SAID USER.

VERIFICATION OF USERS
We verify the identity of the user to the best of our ability but are not liable for false information provided by any of our users.

SUBSCRIPTIONS AND CHARGES

You have the right and responsibility to terminate any subscriptions that you have with ${siteDomain} at any time with no refund payable under any circumstances with the potential loss of your data. Should there have been any error, please inform us as soon as possible. No refunds are provided under any circumstances.

VARIANCE
${siteDomain} reserves the right to vary these terms of use, or transfer liability on the same to third parties with no notice to users on the same. Notice is a courtesy not a requirement.`

    const orientationSteps = [
        {
            placeholder: 'Please read the terms and conditions.',
            content: <div className="text-black whitespace-pre-wrap p-4 space-y-2 overflow-y-auto max-h-96">{termsOfUse}</div>
        },
        {
            placeholder: 'Watch the video for your package.',
            content: <video controls className="w-full max-h-96" src={packageVideos[(profiledata.package1 || '').trim().toUpperCase()] || ''}/>
        },
        {
            placeholder: 'Packages structure overview.',
            content: <video controls className="w-full max-h-96" src={PackagesStructureVideo}/>
        },
        {
            placeholder: 'Understanding match percentage.',
            content: <video controls className="w-full max-h-96" src={MatchPercentageVideo}/>
        },
        {
            placeholder: 'Match percentage details.',
            content: <div className="text-black whitespace-pre-wrap">{matchPercentageText}</div>
        },
        {
            placeholder: 'Profile analysis.',
            content: <video controls className="w-full max-h-96" src={ProfileAnalysisVideo}/>
        },
        {
            placeholder: 'Engagement guidelines.',
            content: <img src={EngagementGuidelinesImg} className="w-full" alt="Engagement"/>
        }
    ]

    let navigate = useNavigate();

    function logout() {
        trackEvent('logout_clicked', { source: 'personal_profile_dashboard' })
        Cookies.remove('auth');
        Cookies.remove('email');
        replaceWithDomainHomepage();
    }

    function checkFirstLogin() {
        if (getLocalStorageItem(orientationKey) !== 'false') {
            setLocalStorageItem(orientationKey, 'false');
        }
    }

    function loadChatsByProfileId(profileId) {
        const url = `/api/get_chats?id=${profileId}`;

        return fetchData(url).then((data) => {
            if (data.responsecode === 200) {
                console.log("Here are all the chats: ", data.chats_data)
                setChatData(data.chats_data)
                setChatLoading(false)
                return data.chats_data
            }
            alert("Something went wrong. Please contact support.")
            return []
        })
    }

    function openChatsModal() {
        if (profiledata.__id) {
            setChatLoading(true)
            loadChatsByProfileId(profiledata.__id).catch((error) => {
                console.error("Error fetching chat data:", error);
                throw error;
            })
        }

        setChatsVisible(true)
    }

    function loadMenProfiles(offset = 0) {
        if (menRequestInFlightRef.current) {
            return Promise.resolve();
        }
        menRequestInFlightRef.current = true;
        if (offset === 0) {
            loadedMenOffsetRef.current = 0;
            setMenLoading(true)
        }
        return fetchData(`/api/get_men?limit=${PROFILE_LOAD_BATCH_SIZE}&offset=${offset}`).then((data) => {
            console.log(data);
            if (data.responsecode === 200) {
                const sortedMen = data.acc.sort((a, b) => Number(b.timestamp) - Number(a.timestamp))
                setMenData((prevMen) => {
                    if (offset === 0) {
                        loadedMenOffsetRef.current = sortedMen.length;
                        return sortedMen;
                    }

                    const seenProfiles = new Set(prevMen.map(getProfileIdentity));
                    const newMen = sortedMen.filter((item) => {
                        const identity = getProfileIdentity(item);
                        if (!identity || seenProfiles.has(identity)) {
                            return false;
                        }
                        seenProfiles.add(identity);
                        return true;
                    });
                    loadedMenOffsetRef.current = offset + sortedMen.length;
                    return [...prevMen, ...newMen];
                })
                setHasMoreMenProfiles(Boolean(data.has_more))
                setMenLoading(false)
            } else {
                alert("Something went wrong. Please contact support.")
            }
        }).catch((error) => {
            console.error("Error fetching purgatory data:", error);
            throw error;
        }).finally(() => {
            menRequestInFlightRef.current = false;
        });
    }

    function loadprofile() {
        const url = `/api/getprofile/${profileid}`;
        removeLocalStorageItem('formData')

        fetchData(url).then((data) => {
            console.log(data);
            if (data.responsecode === 200 && data.data !== false) {
                const profiledata = data.data
                setProfileData(profiledata)
                checkFirstLogin()

                if (profiledata.denialofservice && props.authenticate !== false) {
                    setDosBlocked(true)
                    setLoading(true)
                    return;
                }

                setDosBlocked(false)

                const signedInEmail = String(Cookies.get('email') || '').trim().toLowerCase();
                if (props.authenticate !== false && Cookies.get('auth') && signedInEmail === String(profiledata.EMAIL || '').trim().toLowerCase()
                    && shouldShowLivenessModal(profiledata, Date.now(), getLocalStorageItem(livenessPromptDismissalKey(profiledata)))) {
                    trackEvent('liveness_verification_modal_shown', { email: profiledata.EMAIL });
                    setLivenessModalVisible(true);
                }

                console.log("The notifs:", profiledata.notifications)

                loadChatsByProfileId(profiledata.__id).then(() => {
                    var theguymaybe = searchParams.get('male')
                    theguymaybe = theguymaybe || false
                    console.log("The guy: ", theguymaybe)

                    if (!theguymaybe) {
                        setLoading(false)
                    } else {
                        messageManFunction(Base64.decode(theguymaybe))
                    }
                }).catch((error) => {
                    console.error("Error fetching profile data:", error);
                    throw error;
                });
            } else {
                alert("Something went wrong. Please contact support. You will be redirected to the login page. If you are unable to do so afterwards please contact support.")
                return;
            }
        }).catch((error) => {
            console.error("Error fetching purgatory data:", error);
            throw error;
        });

    }

    function numberofnewmessages() {
        function checkitem(item) {
            const thelastmessage = item.messages[item.messages.length -1]
            
            console.log('The last message:', thelastmessage)
            if (thelastmessage === undefined) {
                return false
            }
            if ((thelastmessage["Guy Sent"]) && !thelastmessage.read) {
                return true
            }
        }

        const evenNumbers = chatdata.filter(checkitem);
        return evenNumbers.length
    }

    function messageManFunction(male) {
        console.log("Starting chat with:", male)
        const url = `/api/getprofile/${Base64.encode(male)}`;
        const setreadurl = `/api/chat/setread`;
        
        const formmData = new FormData()
        formmData.append('malename', male)
        formmData.append('id', profiledata.__id)

        fetchData(url).then((data) => {
            if (data.responsecode === 200) {
                setIndividualChatTitle(data.data)
                setLoading(false)
                setChatsVisible(false)
                setIndividualchatvisible(true)
            } else {
                alert("Something went wrong loading up chat. Please contact support.")
            }
        }).catch((error) => {
            console.error("Error fetching purgatory data:", error);
            throw error;
        });

        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };

        postData(setreadurl,formmData,config).then((data) => {
            if (data.responsecode === 200) {
                console.log('Message set as read')
            } else {
                console.log('Could not set message as read')
            }
        }).catch((err) => {
            console.log("Error in setProfiledataSubmit: ", err);
            throw err;
        });
    }

    function openLivenessVerificationFromNotification() {
        trackEvent('liveness_verification_notification_clicked', { email: profiledata?.EMAIL });
        trackEvent('liveness_verification_modal_shown', { email: profiledata?.EMAIL });
        setNotifModalVisible(false);
        setLivenessModalVisible(true);
    }

    function reset_notifs() {
        const url = `/api/reset_notifications/${Base64.decode(profileid)}`;
        
        fetchData(url).then((data) => {
            console.log(data);
            if (data.responsecode === 200) {
                console.log("Resetting notifications.")
            }
        }).catch((error) => {
            console.error("Error fetching purgatory data:", error);
            throw error;
        });
    }

    function nextOrientationStep() {
        if (orientationStep < orientationSteps.length - 1) {
            setOrientationStep(orientationStep + 1);
        } else {
            setOrientation(false);
            setLocalStorageItem(orientationKey, 'false');
        }
    }

    function decidenotifhintmsg(message) {
        if (message.startsWith('[[')) {
            return "Called"
        } else if (message.startsWith('https://player.mediadelivery.net')) {
            return "A video"
        } else if (message.startsWith('/api/uploads/')) {
            return "Picture"
        } else {
            return message
        }
    }

    React.useEffect(loadprofile, [profileid]);
    React.useEffect(() => {
        if (props.authenticate === false) {
            return () => {};
        }
        return validateAuthCookie();
    }, [props.authenticate, navigate]);
    React.useEffect(() => {
        loadMenProfiles(0)
    }, [profileid]);

    return (
            <>
            {dosBlocked
                ? <>
                    <style>{`
                        @keyframes dos-stall-loading {
                            0% { width: 25%; }
                            70% { width: 75%; }
                            100% { width: 75%; }
                        }
                    `}</style>
                    <div className="fixed top-0 left-0 w-full h-1 z-50 bg-transparent pointer-events-none">
                        <div
                            className="h-full bg-blue-500 w-1/4"
                            style={{ animation: 'dos-stall-loading 2.5s ease-out forwards' }}
                        />
                    </div>
                  </>
                : null
            }
            {(Cookies.get('auth') !== undefined || props.authenticate === false)
                ? <>
                    <LivenessVerificationModal
                        showModal={livenessModalVisible}
                        profile={profiledata}
                        setShowModal={setLivenessModalVisible}
                        onProfileUpdated={(fields) => setProfileData((current) => ({ ...current, ...fields }))}
                    />
                    {/* FOR VIEWING CURRENT CHATS */}
                    <GeneralModal 
                        showModal={chatsvisible} maintitle="Chats" 
                        notdisableproceed={false} canceltext="Back"
                        setShowModal={setChatsVisible} onClose={function (){setIndividualChatTitle({})}}
                    >
                        {chatloading
                            ? <Loader/>
                            : <p className="text-black">{chatdata.length} chats total.</p>
                        }

                        <div className="ees-modal-list">
                            {chatdata.map(function (item, index) {
                                if (item.messages.length !== 0) {
                                    console.log(item)
                                    console.log("Messages: ", item.messages)
                                    console.log(`Messages length: ${item.messages.length}`)

                                    const thelastmessage = item.messages[item.messages.length - 1]
                                    if (thelastmessage["Guy Sent"]) {
                                        return (
                                            <button className="ees-modal-row" onClick={() => messageManFunction(item.male)} key={index}>
                                                <SmartImageComponent preventDownload id="Profile picture" cssclasses="w-10 h-10 rounded-full object-cover" src={"/api/profilepic_by_name/1?NAME="+item.male}/>

                                                <p className="ees-modal-row-main">Him: {decidenotifhintmsg(thelastmessage["Guy Sent"].message)}</p>
                                            </button>
                                        )
                                    }
                                }
                            })}

                            {chatdata.map(function (item, index) {
                                if (item.messages.length !== 0) {
                                    console.log(item)
                                    console.log("Messages: ", item.messages)
                                    console.log(`Messages length: ${item.messages.length}`)

                                    const thelastmessage = item.messages[item.messages.length - 1]
                                    if (!thelastmessage["Guy Sent"]) {
                                        return (
                                            <button className="ees-modal-row" onClick={() => messageManFunction(item.male)} key={index}>
                                                <SmartImageComponent preventDownload id="Profile picture" cssclasses="w-10 h-10 rounded-full object-cover" src={"/api/profilepic_by_name/1?NAME="+item.male}/>

                                                <p className="ees-modal-row-main">You: {decidenotifhintmsg(thelastmessage["Girl Sent"].message)}</p>
                                            </button>
                                        )
                                    }   
                                }
                            })}
                        </div>

                    </GeneralModal>
                    
                    {/* FOR VIEWING INDIVIDUAL CHAT */}
                    <GeneralModal 
                        showModal={individualchatvisible} maintitle={turntoacronym(individualchattitle.fullname1) || ""}
                        notdisableproceed={false} canceltext="Back"
                        setShowModal={setIndividualchatvisible} onClose={function (){setIndividualChatTitle({})}}
                        lilbutton={true} lilbuttonlink={'/profile/'+Base64.encode(individualchattitle.fullname1)+"?id="+profiledata.__id+"&EMAIL="+Base64.encode(profiledata.EMAIL) || ''}
                        lilbuttontext="View Profile"
                        middlebutton={true}
                        middlebuttontext="Call"
                        middlebuttonicon="📞"
                        onMiddleButtonClick={() => {
                            primeCallAudioFromUserGesture();
                            setCallSignal((prev) => prev + 1);
                        }}
                    >
                    {individualchatvisible
                            ? <ChatInterface 
                                    womandata={profiledata}
                                    male={individualchattitle}
                                    chatdata={[]}
                                    callSignal={callSignal}
                                />
                            : null
                    }
                    </GeneralModal>
                    
                    {/* FOR NOTIFICATIONS */}
                    <GeneralModal 
                        showModal={notifmodalvisible} maintitle="Notifications" 
                        notdisableproceed={false} canceltext="Back"
                        setShowModal={setNotifModalVisible} onClose={function (){setIndividualChatTitle({})}}
                    >
                        {profiledata.notifications !== undefined || shouldShowLivenessNotification(profiledata)
                            ? <>
                                {shouldShowLivenessNotification(profiledata)
                                    ? <div className="ees-modal-list mb-3">
                                        <button type="button" className="ees-modal-row" onClick={openLivenessVerificationFromNotification}>
                                            <div className="ees-modal-row-main">
                                                <p className="text-black text-base font-semibold">Complete profile verification</p>
                                                <p className="ees-modal-row-meta">Record your liveness video to receive your verified badge after review.</p>
                                            </div>
                                        </button>
                                      </div>
                                    : null
                                }
                                {(!profiledata.notifications || profiledata.notifications.length === 0) && !shouldShowLivenessNotification(profiledata)
                                    ? <p className="text-black">No notifications currently</p>
                                    : null
                                }
                                {profiledata.notifications && profiledata.notifications.length > 0
                                    ? <div className="ees-modal-list">
                                        {Object.prototype.toString.call(profiledata.notifications) === '[object Array]'
                                            ? <>
                                                {profiledata.notifications.map(function (item) {
                                                    return (
                                                        <div className="ees-modal-row">
                                                            <div className="ees-modal-row-main">
                                                                <NotificationText text={item.text} />
                                                                <p className="ees-modal-row-meta">{timestamptodate(item.timestamp)}</p>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </>
                                            : <>
                                                {profiledata.notifications.includes("[object Object]")
                                                    ? <p className="text-black">No current notifications.</p>
                                                    : null
                                                }
                                              </>
                                        }
                                    </div>
                                    : null
                                }
                            </>
                            : <p className="text-black">No notifications currently</p>
                        }
                    </GeneralModal>

                    <div className={dashboardClassName}>
                        <style>{`
                            .ees-profile-dashboard{--ink:#171411;--ink2:#625951;--paper:#f6f1e9;--paper2:#fffaf3;--wine:#3a1a22;--wine2:#512430;--wine3:#1b0e12;--gold:#bd9154;--gold2:#e4c79d;--gold3:#f2e4cc;--line:rgba(23,20,17,.10);--shadow:0 18px 55px rgba(37,24,23,.12);--serif:Georgia,"Times New Roman",serif;--sans:Inter,ui-sans-serif,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;min-height:100vh;background:var(--paper);color:var(--ink);font-family:var(--sans);text-transform:none}.ees-profile-dashboard *{box-sizing:border-box}.ees-profile-dashboard button{cursor:pointer}.ees-profile-dashboard a{text-decoration:none;color:inherit}.ees-profile-app{min-height:100vh;display:grid;grid-template-columns:258px 1fr}.ees-sidebar{min-height:100vh;position:sticky;top:0;height:100vh;background:radial-gradient(circle at 25% 3%,rgba(228,199,157,.10),transparent 25%),linear-gradient(180deg,#251216,#160c0f 68%,#10090b);color:#fff;padding:28px 22px;display:flex;flex-direction:column}.ees-brand{display:flex;align-items:center;gap:12px;padding:2px 6px 28px;border-bottom:1px solid rgba(255,255,255,.08)}.ees-mark{width:42px;height:42px;border:1px solid rgba(228,199,157,.7);display:grid;place-items:center;font-family:var(--serif);font-size:14px;color:var(--gold3);position:relative}.ees-mark:after{content:"";position:absolute;inset:4px;border:1px solid rgba(228,199,157,.18)}.ees-brandtext{display:grid;text-transform:uppercase;line-height:1.08;letter-spacing:.12em}.ees-brandtext b{font-size:12px}.ees-brandtext span{font-size:8px;letter-spacing:.30em;color:var(--gold2);margin-top:5px}.ees-nav{display:grid;gap:7px;margin:30px 0}.ees-nav a,.ees-nav button{display:flex;align-items:center;gap:12px;padding:12px 13px;border-radius:12px;color:rgba(255,255,255,.62);font-size:13px;border:0;background:transparent;text-align:left}.ees-nav a:hover,.ees-nav button:hover{background:rgba(255,255,255,.055);color:#fff}.ees-nav .active{background:linear-gradient(90deg,rgba(189,145,84,.18),rgba(189,145,84,.06));color:#fff;border:1px solid rgba(228,199,157,.12)}.ees-nav svg,.ees-iconbtn svg,.ees-mobile-nav svg{width:18px;height:18px;stroke-width:1.8}.ees-nav .active svg{color:var(--gold2)}.ees-sidecard{margin-top:auto;border:1px solid rgba(228,199,157,.14);background:rgba(255,255,255,.035);padding:16px;border-radius:16px}.ees-sidecard .ees-eyebrow{color:var(--gold2);margin:0 0 6px}.ees-sidecard strong{font-family:var(--serif);font-size:18px;font-weight:400}.ees-sidecard p{font-size:11px;line-height:1.55;color:rgba(255,255,255,.48);margin:7px 0 13px}.ees-sidecard button{width:100%;border:1px solid rgba(228,199,157,.36);background:transparent;color:#fff;border-radius:999px;padding:9px;font-size:10px;text-transform:uppercase;letter-spacing:.12em}.ees-main{min-width:0}.ees-topbar{height:74px;display:flex;align-items:center;justify-content:space-between;padding:0 clamp(24px,4vw,54px);border-bottom:1px solid var(--line);background:rgba(246,241,233,.88);backdrop-filter:blur(12px);position:sticky;top:0;z-index:20}.ees-page-title{display:flex;align-items:baseline;gap:11px}.ees-page-title h1{margin:0;font-family:var(--serif);font-weight:400;font-size:27px;color:var(--wine3)}.ees-page-title span{font-size:11px;color:#92877f}.ees-top-actions{display:flex;align-items:center;gap:10px}.ees-iconbtn{width:38px;height:38px;border:1px solid var(--line);background:rgba(255,255,255,.56);border-radius:12px;display:grid;place-items:center;color:#6c625b;position:relative}.ees-dot{position:absolute;right:8px;top:8px;width:6px;height:6px;border-radius:50%;background:var(--gold);box-shadow:0 0 0 3px var(--paper)}.ees-miniavatar{width:38px;height:38px;border-radius:50%;overflow:hidden;display:grid;place-items:center;background:linear-gradient(145deg,var(--wine2),var(--wine3));color:var(--gold3);font-family:var(--serif);font-size:13px;border:2px solid #fff;box-shadow:0 0 0 1px rgba(23,20,17,.1)}.ees-miniavatar img{width:100%;height:100%;object-fit:cover}.ees-content{padding:34px clamp(24px,4vw,54px) 60px;max-width:1500px;margin:auto}.ees-hero{position:relative;overflow:hidden;border-radius:26px;min-height:238px;color:#fff;background:radial-gradient(circle at 90% 10%,rgba(228,199,157,.18),transparent 28%),linear-gradient(135deg,#4c202b 0%,#281318 52%,#180d10 100%);padding:33px 36px;box-shadow:0 26px 66px rgba(50,23,29,.18);display:grid;grid-template-columns:1fr auto;align-items:end}.ees-hero:after{content:"";position:absolute;width:380px;height:380px;border:1px solid rgba(228,199,157,.12);border-radius:50%;right:-190px;bottom:-230px;box-shadow:0 0 0 50px rgba(228,199,157,.025),0 0 0 100px rgba(228,199,157,.018)}.ees-user{display:flex;align-items:center;gap:21px;position:relative;z-index:1}.ees-avatar{width:98px;height:98px;border-radius:50%;overflow:hidden;display:grid;place-items:center;background:linear-gradient(145deg,#734151,#2f1820);border:3px solid var(--gold2);box-shadow:0 0 0 5px rgba(255,255,255,.07);font-family:var(--serif);font-size:29px;color:var(--gold3)}.ees-avatar img{width:100%;height:100%;object-fit:cover;object-position:top}.ees-user h2{margin:4px 0 4px;font-family:var(--serif);font-size:33px;font-weight:400}.ees-user p{margin:0;color:rgba(255,255,255,.6);font-size:12px}.ees-eyebrow{text-transform:uppercase;letter-spacing:.16em;font-size:9px;font-weight:800}.ees-user .ees-eyebrow{color:var(--gold2)}.ees-hero-actions{display:flex;gap:9px;position:relative;z-index:2}.ees-hero-actions a,.ees-hero-actions button{height:42px;border-radius:999px;padding:0 18px;text-transform:uppercase;letter-spacing:.12em;font-size:9px;font-weight:850;display:inline-flex;align-items:center;justify-content:center}.ees-btn-gold{border:0;background:linear-gradient(110deg,#ae7d43,var(--gold2),#c69b61);color:#21150f}.ees-btn-ghost{border:1px solid rgba(255,255,255,.18);background:rgba(255,255,255,.055);color:#fff}.ees-section-head{display:flex;align-items:end;justify-content:space-between;gap:18px;margin:36px 0 17px}.ees-section-head h3{font-family:var(--serif);font-size:28px;font-weight:400;color:var(--wine3);margin:0}.ees-section-head p{margin:5px 0 0;color:#81776f;font-size:12px}.ees-filters{display:flex;gap:9px}.ees-search{position:relative}.ees-search input{width:220px;height:40px;border:1px solid var(--line);border-radius:12px;background:rgba(255,255,255,.68);padding:0 12px 0 37px;outline:none;color:var(--ink)}.ees-search svg{position:absolute;width:16px;height:16px;left:13px;top:12px;color:#8c8179;stroke-width:1.8}.ees-filters select{height:40px;border:1px solid var(--line);border-radius:12px;background:rgba(255,255,255,.68);padding:0 33px 0 12px;color:#665c55;outline:none}.ees-match-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:15px}.ees-profile-card{background:rgba(255,250,243,.86);border:1px solid rgba(23,20,17,.085);border-radius:20px;padding:21px;box-shadow:0 10px 28px rgba(43,31,27,.045);transition:.2s ease}.ees-profile-card:hover{transform:translateY(-3px);box-shadow:var(--shadow);border-color:rgba(189,145,84,.3)}.ees-cardtop{display:flex;justify-content:space-between;gap:12px;align-items:flex-start;margin-bottom:18px}.ees-person{display:flex;gap:12px;align-items:center;min-width:0}.ees-personavatar{width:50px;height:50px;flex:0 0 auto;border-radius:50%;overflow:hidden;display:grid;place-items:center;background:linear-gradient(145deg,#efe2cf,#d8b98d);color:var(--wine);font-family:var(--serif);font-size:15px;border:1px solid rgba(189,145,84,.34)}.ees-personimg{width:100%;height:100%;object-fit:cover;object-position:top}.ees-person h4{margin:0 0 4px;font-family:var(--serif);font-size:19px;font-weight:400;color:var(--wine3)}.ees-person p{margin:0;font-size:10px;color:#91867e;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.ees-package-pill{flex:0 0 auto;padding:7px 8px;border-radius:999px;background:#f2e5d2;color:#6b4a27;font-size:9px;font-weight:850}.ees-card-actions{display:grid;grid-template-columns:1fr 1fr;gap:8px}.ees-card-actions a,.ees-card-actions button{height:36px;border-radius:10px;font-size:9px;text-transform:uppercase;letter-spacing:.08em;font-weight:850;display:flex;align-items:center;justify-content:center}.ees-view{border:1px solid rgba(52,25,31,.15);background:#fff;color:var(--wine)}.ees-message{border:0;background:var(--wine);color:#fff}.ees-message:hover{background:var(--wine2)}.ees-view:hover{border-color:var(--gold)}.ees-offline,.ees-load-more{margin-top:10px;width:100%;height:36px;border-radius:10px;border:1px solid rgba(52,25,31,.15);background:#fff;color:var(--wine);font-size:9px;text-transform:uppercase;letter-spacing:.08em;font-weight:850}.ees-mobile-nav{display:none}.ees-mobile-nav button,.ees-mobile-nav a{border:0;background:transparent;color:rgba(255,255,255,.54);padding:7px 3px;font-size:8px;display:grid;place-items:center;gap:3px}.ees-mobile-nav .active{color:var(--gold2)}@media(max-width:1100px){.ees-match-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.ees-hero{grid-template-columns:1fr}.ees-hero-actions{margin-top:24px}}@media(max-width:820px){.ees-profile-app{grid-template-columns:1fr}.ees-sidebar{display:none}.ees-topbar{height:66px;padding:0 18px}.ees-page-title span{display:none}.ees-content{padding:20px 16px 94px}.ees-hero{border-radius:20px;padding:24px 20px}.ees-user{align-items:flex-start}.ees-avatar{width:72px;height:72px;font-size:22px}.ees-user h2{font-size:26px}.ees-hero-actions{width:100%}.ees-hero-actions a,.ees-hero-actions button{flex:1}.ees-section-head{align-items:flex-start;flex-direction:column}.ees-filters{width:100%}.ees-search{flex:1}.ees-search input{width:100%}.ees-match-grid{grid-template-columns:1fr}.ees-mobile-nav{display:grid;grid-template-columns:repeat(5,1fr);position:fixed;left:10px;right:10px;bottom:10px;z-index:50;background:rgba(24,13,16,.95);backdrop-filter:blur(14px);border:1px solid rgba(228,199,157,.12);border-radius:18px;padding:7px}}@media(max-width:480px){.ees-filters select{max-width:122px}.ees-hero-actions{flex-direction:column}.ees-card-actions{grid-template-columns:1fr}.ees-page-title h1{font-size:24px}}
                            .uh-profile-dashboard{--ink:#ffffff;--ink2:#b9b9b9;--paper:#050505;--paper2:#101010;--wine:#ff58cf;--wine2:#ff89dd;--wine3:#050505;--gold:#d8b45a;--gold2:#f0d88c;--gold3:#f0d88c;--line:rgba(216,180,90,.22);--shadow:0 22px 62px rgba(255,88,207,.16);background:radial-gradient(circle at 50% -10%,rgba(255,88,207,.10),transparent 28%),radial-gradient(circle at 15% 18%,rgba(216,180,90,.08),transparent 24%),#050505!important;color:#fff}.uh-profile-dashboard .ees-sidebar{background:radial-gradient(circle at 18% 0,rgba(255,88,207,.16),transparent 29%),linear-gradient(180deg,#101010,#050505 70%);border-right:1px solid rgba(216,180,90,.18)}.uh-profile-dashboard .ees-mark{border-color:rgba(240,216,140,.72);color:var(--gold2)}.uh-profile-dashboard .ees-brandtext span,.uh-profile-dashboard .ees-eyebrow,.uh-profile-dashboard .ees-user .ees-eyebrow,.uh-profile-dashboard .ees-nav .active svg{color:var(--gold2)}.uh-profile-dashboard .ees-nav .active{background:linear-gradient(90deg,rgba(255,88,207,.20),rgba(216,180,90,.08));border-color:rgba(216,180,90,.22)}.uh-profile-dashboard .ees-sidecard,.uh-profile-dashboard .ees-profile-card{background:linear-gradient(180deg,rgba(255,255,255,.055),rgba(255,255,255,.025));border-color:rgba(216,180,90,.20);color:#fff}.uh-profile-dashboard .ees-sidecard p,.uh-profile-dashboard .ees-section-head p,.uh-profile-dashboard .ees-person p,.uh-profile-dashboard .ees-page-title span{color:rgba(255,255,255,.62)}.uh-profile-dashboard .ees-topbar{background:rgba(5,5,5,.88);border-bottom-color:rgba(216,180,90,.18)}.uh-profile-dashboard .ees-page-title h1,.uh-profile-dashboard .ees-section-head h3,.uh-profile-dashboard .ees-person h4{color:#fff}.uh-profile-dashboard .ees-hero{background:radial-gradient(circle at 92% 12%,rgba(255,88,207,.20),transparent 30%),linear-gradient(135deg,#ff58cf 0%,#8c185f 42%,#101010 100%);box-shadow:0 26px 66px rgba(255,88,207,.14)}.uh-profile-dashboard .ees-avatar,.uh-profile-dashboard .ees-miniavatar{background:linear-gradient(145deg,#ff89dd,#101010);border-color:var(--gold2);color:#fff}.uh-profile-dashboard .ees-personavatar{background:linear-gradient(145deg,#ff89dd,#d8b45a);color:#050505}.uh-profile-dashboard .ees-btn-gold{background:linear-gradient(110deg,#d8b45a,#f0d88c,#ff89dd);color:#050505}.uh-profile-dashboard .ees-message{background:linear-gradient(135deg,#ff58cf,#8c185f);color:#fff}.uh-profile-dashboard .ees-view,.uh-profile-dashboard .ees-offline,.uh-profile-dashboard .ees-load-more,.uh-profile-dashboard .ees-filters select,.uh-profile-dashboard .ees-search input,.uh-profile-dashboard .ees-iconbtn{background:rgba(255,255,255,.08);border-color:rgba(216,180,90,.22);color:#fff}.uh-profile-dashboard .ees-package-pill{background:rgba(216,180,90,.16);color:var(--gold2)}.uh-profile-dashboard .ees-mobile-nav{background:rgba(5,5,5,.95);border-color:rgba(216,180,90,.24)}.uh-profile-dashboard .ees-mobile-nav .active{color:var(--gold2)}
                            .ees-profile-dashboard{position:fixed;inset:0;z-index:20;overflow:auto;width:100vw;background:var(--paper)!important}.ees-profile-app{display:block!important;min-height:100vh!important}.ees-sidebar{display:none!important}.ees-main{min-height:100vh;padding-bottom:96px!important}.ees-mobile-nav{display:grid!important;position:fixed!important;left:0!important;right:0!important;bottom:0!important;width:100%!important;max-width:none!important;transform:none!important;border-radius:18px 18px 0 0!important;grid-template-columns:repeat(4,1fr)!important}
                        `}</style>
                        {dosBlocked ? <div className="w-full h-1 bg-blue-500"></div> : null}
                        <div className="ees-profile-app">
                            <aside className="ees-sidebar">
                                <div className="ees-brand">
                                    <div className="ees-mark">{dashboardBrand.mark}</div>
                                    <div className="ees-brandtext"><b>{dashboardBrand.firstLine}</b><span>{dashboardBrand.secondLine}</span></div>
                                </div>
                                <nav className="ees-nav">
                                    <Link to="/"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 11.5 12 4l9 7.5"/><path d="M5.5 10.5V20h13v-9.5"/></svg>Discover</Link>
                                    <a className="active" href="#matches"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z"/></svg>Matches</a>
                                    <button type="button" onClick={openChatsModal}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z"/></svg>Messages</button>
                                    <Link to={'/profile/'+profileid}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>My Profile</Link>
                                    <button type="button" onClick={() => {setNotifModalVisible(true);reset_notifs()}}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M18 8a6 6 0 1 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M10 21h4"/></svg>Notifications</button>
                                </nav>
                                <div className="ees-sidecard">
                                    <p className="ees-eyebrow">Private membership</p>
                                    <strong>{profiledata.package1 ? `${profiledata.package1} member` : 'Your private account'}</strong>
                                    <p>Your dashboard is tuned for genuine introductions and private communication.</p>
                                    <Link className="ees-btn-ghost" to={'/profile/'+profileid}>Preview profile</Link>
                                </div>
                            </aside>

                            <main className="ees-main">
                                <header className="ees-topbar">
                                    <div className="ees-page-title"><h1>My Profile</h1><span>Member dashboard</span></div>
                                    <div className="ees-top-actions">
                                        <button className="ees-iconbtn" aria-label="Notifications" onClick={() => {setNotifModalVisible(true);reset_notifs()}}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M18 8a6 6 0 1 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M10 21h4"/></svg>{(profiledata.notifications && profiledata.notifications.length) || shouldShowLivenessNotification(profiledata) ? <span className="ees-dot"></span> : null}</button>
                                        <div className="ees-miniavatar">{loading ? turntoacronym(profiledata.fullname1 || profiledata.EMAIL || 'ME') : <SmartImageComponent preventDownload src={"/api/profilepic/1?EMAIL="+profiledata.EMAIL} cssclasses="" id="Mini profile picture" backup={true}/>}</div>
                                    </div>
                                </header>

                                <div className="ees-content">
                                    <section className="ees-hero">
                                        <div className="ees-user">
                                            <div className="ees-avatar">{loading ? turntoacronym(profiledata.fullname1 || profiledata.EMAIL || 'ME') : <SmartImageComponent preventDownload src={"/api/profilepic/1?EMAIL="+profiledata.EMAIL} cssclasses="" id="Dashboard profile picture" backup={true}/>}</div>
                                            <div>
                                                <p className="ees-eyebrow">{dashboardBrand.memberLabel}</p>
                                                <h2>{profiledata.fullname1 ? turntoacronym(profiledata.fullname1) : 'Your profile'}</h2>
                                            </div>
                                        </div>
                                        <div className="ees-hero-actions">
                                            <Link className="ees-btn-ghost" to={'/profile/'+profileid}>Preview profile</Link>
                                            <button className="ees-btn-gold" type="button" onClick={() => navigate('/signup')}>Edit profile</button>
                                            <button className="ees-btn-ghost" type="button" onClick={logout}>Log out</button>
                                        </div>
                                    </section>

                                    <div className="ees-section-head" id="matches">
                                        <div>
                                            <h3>{dashboardBrand.introductionsTitle}</h3>
                                            <p>Profiles selected around your preferences and compatibility.</p>
                                        </div>
                                        <div className="ees-filters">
                                            <div className="ees-search">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/></svg>
                                                <input type="search" placeholder="Search profiles" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                                            </div>
                                            <select value={mainpackage} onChange={(e) => setMainPackage(e.target.value)}>
                                                <option value="">Best match</option>
                                                {packages.map((item) => <option key={item} value={item}>{item}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    <section className="ees-match-grid">
                                        {loading || menloading
                                            ? <Loader />
                                            : <IndividualSection
                                                dashboard
                                                section={"Your Matches"}
                                                men={mendata.filter(item => item.gender1 === "Male")}
                                                minage={minage}
                                                maxage={maxage}
                                                country={country}
                                                mainpackage={mainpackage}
                                                relationshipstatus={relationshipstatus}
                                                setIndividualchatvisible={setIndividualchatvisible}
                                                setIndividualChatTitle={setIndividualChatTitle}
                                                herid={profiledata.__id}
                                                heremail={profiledata.EMAIL}
                                                herpackage={profiledata.package1}
                                                hiddenProfiles={profiledata.hidden_profiles}
                                                searchTerm={searchTerm}
                                                batchSize={PROFILE_LOAD_BATCH_SIZE}
                                                hasMoreProfiles={hasMoreMenProfiles}
                                                onLoadMoreProfiles={() => loadMenProfiles(loadedMenOffsetRef.current)}
                                              />
                                        }
                                    </section>
                                </div>
                            </main>
                        </div>

                        <nav className="ees-mobile-nav">
                            <a className="active" href="#matches"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z"/></svg>Matches</a>
                            <button type="button" onClick={openChatsModal}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z"/></svg>Messages</button>
                            <Link to={'/profile/'+profileid}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>Profile</Link>
                            <button type="button" onClick={() => {setNotifModalVisible(true);reset_notifs()}}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M18 8a6 6 0 1 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/></svg>Alerts</button>
                        </nav>
                    </div>
                  </>
                : <Navigate to="/" replace />
            }
            </>
        )
}
